import React from 'react'
import { aiMessageOf } from '../outreachEvidence.js';
const { useState, useEffect, useCallback, useMemo, useRef } = React;

import {
  AirtableAPI, TABLE_IDS, CURRENT_USER, COMPANY_PROFILE,
  callOpenAI, navSetUrl, SOURCE_OPTIONS, OPP_STAGES, WON_STAGES, CLOSED_STAGES,
  BENCH_REPLY_HIGH, BENCH_REPLY_LOW, BENCH_MEETING_HIGH, BENCH_MEETING_LOW,
  CHANNEL_BENCHMARKS, MESSAGE_PROMPTS, MESSAGE_PROMPT_DEFAULTS, saveMessagePrompts,
  resolvePromptTemplate, saveCompanyProfile, channelIcon, logoutUser,
  CLIENT_CONFIG, AUTH_TOKEN, loadBranding, BRANDING_LS_KEY,
} from '../globals.js';
import {
  F, linkedIds, resolveLinked, InfoTip, REPLY_STATUSES, computeEnrichment,
  DIAGNOSIS_CONFIG, findDuplicateStakeholder, confirmDuplicateStakeholder,
  deriveStakeholderStatus, updateStakeholderStatus, STAKEHOLDER_STATUS_PRIORITY,
  STAKEHOLDER_STATUS_PROTECTED, activateAccountIfNeeded,
  formatCurrency, formatDate, strSimilarity, FileNotesRenderer,
} from '../utils.jsx';
import StakeholderHistoryModal from './StakeholderHistoryModal.jsx';
import AIMessageModal from './AIMessageModal.jsx';
import ContentLab from './ContentLab.jsx';


function CampaignsHub({ data, api, onLogActivity, onAddRecord, onUpdateRecord, onDeleteRecord, campaignPrefill, clearCampaignPrefill, goToMessageLab }) {
  const { campaigns = [], stakeholders = [], accounts = [], outreach = [], events = [], landings = [] } = data;

  const [selectedId, setSelectedId] = useState(null);
  const [listSearch, setListSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingCampaign, setEditingCampaign] = useState(null);
  const [form, setForm] = useState({ name:'', type:'White Paper', status:'Draft', messageTemplate:'', assetUrl:'', startDate:'', goal:'', context:'', assignedIds: [] });
  const [saving, setSaving] = useState(false);
  const [hubTab, setHubTab] = useState('campaigns');
  const [tplExpanded, setTplExpanded] = useState(false);
  const [tplOpen, setTplOpen] = useState(false);
  const [ctxExpanded, setCtxExpanded] = useState(false);
  const [sumExpanded, setSumExpanded] = useState(false);
  const [briefTab, setBriefTab] = useState('context');

  // Handle campaignPrefill coming from Report Builder (Create campaign from insight)
  useEffect(() => {
    if (campaignPrefill) {
      setEditingCampaign(null);
      setSelectedId(null);
      setForm({
        name: campaignPrefill.name || '',
        type: campaignPrefill.type || 'White Paper',
        status: 'Draft',
        messageTemplate: '',
        assetUrl: '',
        startDate: '',
        goal: '',
        context: campaignPrefill.context || '',
        assignedIds: campaignPrefill.stakeholderIds || [],
      });
      setShowForm(true);
      if (clearCampaignPrefill) clearCampaignPrefill();
    }
  }, [campaignPrefill, clearCampaignPrefill]);
  const [filterIndustry, setFilterIndustry] = useState('');
  const [filterRole, setFilterRole] = useState('');
  const [filterSearch, setFilterSearch] = useState('');
  const [invitePreview, setInvitePreview] = useState(null); // {id, msg, generating}
  const [msgModalStk, setMsgModalStk] = useState(null);

  // Campaign Context + AI Summary state
  const [editingContext, setEditingContext] = useState(false);
  const [contextDraft, setContextDraft] = useState('');
  const [savingContext, setSavingContext] = useState(false);
  const [generatingSummary, setGeneratingSummary] = useState(false);
  const [campaignUploadingFile, setCampaignUploadingFile] = useState(false);

  // Message Template inline edit
  const [editingTemplate, setEditingTemplate] = useState(false);
  const [templateDraft, setTemplateDraft] = useState('');
  const [savingTemplate, setSavingTemplate] = useState(false);

  const saveCampaignContext = async () => {
    if (!selectedCampaign) return;
    setSavingContext(true);
    try {
      const a = api || new AirtableAPI();
      await a.updateRecord(TABLE_IDS.campaigns, selectedCampaign.id, { 'Context': contextDraft });
      if (onUpdateRecord) onUpdateRecord('campaigns', selectedCampaign.id, { 'Context': contextDraft });
      setEditingContext(false);
      if (onLogActivity) onLogActivity();
    } catch (e) {
      console.error('[saveCampaignContext] Error:', e);
      window.__oikeToast('Failed to save context: ' + (e.message || 'unknown'), 'error');
    }
    setSavingContext(false);
  };

  const saveCampaignTemplate = async () => {
    if (!selectedCampaign) return;
    setSavingTemplate(true);
    try {
      const a = api || new AirtableAPI();
      await a.updateRecord(TABLE_IDS.campaigns, selectedCampaign.id, { 'Message Template': templateDraft });
      if (onUpdateRecord) onUpdateRecord('campaigns', selectedCampaign.id, { 'Message Template': templateDraft });
      setEditingTemplate(false);
      if (onLogActivity) onLogActivity();
    } catch (e) {
      window.__oikeToast('Failed to save template: ' + (e.message || 'unknown'), 'error');
    }
    setSavingTemplate(false);
  };


  // File upload → AI summary → append to Campaign "Context" as FILE: block
  const handleCampaignFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !selectedCampaign) return;
    setCampaignUploadingFile(true);
    try {
      const text = await file.text();
      const prompt = `Summarize the following file content. Extract the most relevant points for a B2B sales team running this campaign. Be concise (5-8 bullets max).\n\nFile: ${file.name}\n\n${text.slice(0, 8000)}`;
      const summary = await callOpenAI({ prompt, temperature: 0.4, max_tokens: 500 });
      const dateStr = new Date().toLocaleDateString('en-GB');
      const entry = `\n\n📎 FILE: ${file.name} (uploaded ${dateStr})\n${summary}`;
      const currentContext = F(selectedCampaign, 'Context') || '';
      const updated = (currentContext + entry).trim();
      const a = api || new AirtableAPI();
      await a.updateRecord(TABLE_IDS.campaigns, selectedCampaign.id, { 'Context': updated });
      if (onUpdateRecord) onUpdateRecord('campaigns', selectedCampaign.id, { 'Context': updated });
      if (onLogActivity) onLogActivity();
    } catch (err) {
      console.error('Campaign file upload failed:', err);
      window.__oikeToast('File upload failed — try again with a smaller file or a different format.', 'error');
    }
    setCampaignUploadingFile(false);
    e.target.value = '';
  };

  const generateCampaignSummary = async () => {
    if (!selectedCampaign) return;
    const ctx = F(selectedCampaign, 'Context') || '';
    if (!ctx.trim()) {
      window.__oikeToast('Add campaign context first — the AI needs something to work with.', 'warning');
      return;
    }
    setGeneratingSummary(true);
    try {
      const name = F(selectedCampaign, 'Name') || '';
      const type = F(selectedCampaign, 'Type') || '';
      const template = F(selectedCampaign, 'Message Template') || '';
      const asset = F(selectedCampaign, 'Asset URL') || '';
      const notes = F(selectedCampaign, 'Notes') || '';

      const prompt = `You are a senior B2B sales strategist for ${COMPANY_PROFILE.companyName} (${COMPANY_PROFILE.services}).

Write a concise strategic summary (under 180 words) of this outreach campaign. Focus on: who should be targeted, what pain this asset solves, the best opening angle, and what outcome we want per touch. Be specific and actionable — this summary will be used as context when personalizing individual messages.

CAMPAIGN: ${name}
TYPE: ${type}
ASSET: ${asset || 'None provided'}

MANUAL CONTEXT (user-written):
${ctx}

${template ? `REFERENCE MESSAGE TEMPLATE:\n${template.slice(0, 400)}` : ''}
${notes ? `ADDITIONAL NOTES:\n${notes.slice(0, 400)}` : ''}

Format as 3-4 short sections with ### headers: Target, Angle, Pain Addressed, Desired Outcome. Use **bold** for key phrases. No fluff.`;

      const summary = await callOpenAI({ prompt, temperature: 0.6, max_tokens: 500 });
      const a = api || new AirtableAPI();
      await a.updateRecord(TABLE_IDS.campaigns, selectedCampaign.id, { 'AI Summary': summary });
      if (onLogActivity) onLogActivity();
    } catch (e) {
      console.error('[generateCampaignSummary] Error:', e);
      window.__oikeToast('Failed to generate summary: ' + (e.message || 'unknown'), 'error');
    }
    setGeneratingSummary(false);
  };

  const TYPE_OPTIONS = ['White Paper', 'News / Article', 'Product Launch', 'Case Study', 'Cold Outreach'];
  const STATUS_OPTIONS = ['Draft', 'Active', 'Paused', 'Completed'];
  const STATUS_COLORS = { 'Draft': '#6B5CA5', 'Active': '#1E7B4F', 'Paused': '#9A6212', 'Completed': '#2D5F99' };
  const TYPE_ICONS = { 'White Paper': '📄', 'News / Article': '📰', 'Product Launch': '🚀', 'Case Study': '📊', 'Cold Outreach': '🎯' };

  const selectedCampaign = campaigns.find(c => c.id === selectedId) || null;
  const reachedIds = selectedCampaign ? linkedIds(selectedCampaign, 'Stakeholders Reached') : [];
  // Assigned = curated list of contacts for this campaign. Includes legacy Reached for backward compatibility.
  const assignedIdsRaw = selectedCampaign ? linkedIds(selectedCampaign, 'Assigned Stakeholders') : [];
  const assignedIds = [...new Set([...assignedIdsRaw, ...reachedIds])];

  // ── Filtered campaigns list ──
  const filteredCampaigns = campaigns.filter(c => {
    if (statusFilter && F(c,'Status') !== statusFilter) return false;
    if (listSearch && !(F(c,'Name')||'').toLowerCase().includes(listSearch.toLowerCase())) return false;
    return true;
  }).sort((a,b) => (F(b,'Start Date')||'').localeCompare(F(a,'Start Date')||''));

  // ── Filter options for detail view ──
  const industryOptions = [...new Set(accounts.map(a => F(a,'Industry')).filter(Boolean))].sort();
  const roleOptions = [...new Set(stakeholders.map(s => F(s,'Role')).filter(Boolean))].sort();

  // ── Detail contacts: ONLY those assigned to this campaign (curated) ──
  const detailContacts = selectedCampaign ? stakeholders.filter(s => {
    if (!assignedIds.includes(s.id)) return false; // Only assigned contacts
    if (filterSearch) {
      const q = filterSearch.toLowerCase();
      if (!(`${F(s,'Name')||''} ${F(s,'Last name')||''} ${F(s,'Role')||''}`).toLowerCase().includes(q)) return false;
    }
    if (filterRole && F(s,'Role') !== filterRole) return false;
    if (filterIndustry) {
      const acc = accounts.find(a => linkedIds(s,'Account').includes(a.id));
      if (!acc || F(acc,'Industry') !== filterIndustry) return false;
    }
    return true;
  }).sort((a,b) => {
    const aR = reachedIds.includes(a.id), bR = reachedIds.includes(b.id);
    if (aR !== bR) return aR ? 1 : -1; // Pending first, reached last
    return (F(a,'Name')||'').localeCompare(F(b,'Name')||'');
  }) : [];

  // ── Add / Remove contacts from campaign ──
  const [showAddContacts, setShowAddContacts] = useState(false);
  const [addContactsSearch, setAddContactsSearch] = useState('');
  const [addingContactId, setAddingContactId] = useState(null);
  const [selectedToAdd, setSelectedToAdd] = useState(new Set()); // bulk selection
  const [bulkAdding, setBulkAdding] = useState(false);
  const [campaignHistoryStk, setCampaignHistoryStk] = useState(null); // for StakeholderHistoryModal
  const [showEmailTpl, setShowEmailTpl] = useState(false);
  const [showCampaignLandings, setShowCampaignLandings] = useState(false);
  const [emailTplHtml, setEmailTplHtml] = useState('');
  const [emailTplDirty, setEmailTplDirty] = useState(false);
  const [emailTplSubject, setEmailTplSubject] = useState('');
  const [generatingTpl, setGeneratingTpl] = useState(false);
  const [savingTpl, setSavingTpl] = useState(false);
  const [previewTpl, setPreviewTpl] = useState(false);
  const [emailTplContent, setEmailTplContent] = useState(null); // parsed sections for visual editor
  const [tplLanguage, setTplLanguage] = useState('en');
  const [editingAssetUrl, setEditingAssetUrl] = useState(false);
  const [assetUrlDraft, setAssetUrlDraft] = useState('');
  const [savingAssetUrl, setSavingAssetUrl] = useState(false);

  const saveAssetUrl = async () => {
    if (!selectedCampaign) return;
    setSavingAssetUrl(true);
    try {
      const a = api || new AirtableAPI();
      await a.updateRecord(TABLE_IDS.campaigns, selectedCampaign.id, { 'Asset URL': assetUrlDraft });
      if (onUpdateRecord) onUpdateRecord('campaigns', selectedCampaign.id, { 'Asset URL': assetUrlDraft });
      setEditingAssetUrl(false);
    } catch(e) { window.__oikeToast('Failed to save URL: ' + e.message, 'error'); }
    setSavingAssetUrl(false);
  };

  const downloadHtml = () => {
    if (!emailTplHtml) return;
    const blob = new Blob([emailTplHtml], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `${(selectedCampaign && F(selectedCampaign,'Name')) || 'email-template'}.html`;
    a.click(); URL.revokeObjectURL(url);
  };

  const downloadPdf = () => {
    if (!emailTplHtml) return;
    const w = window.open('', '_blank');
    w.document.write(emailTplHtml);
    w.document.close();
    w.focus();
    setTimeout(() => { w.print(); }, 600);
  };

  const addContactToCampaign = async (stakeholderId) => {
    if (!selectedCampaign) return;
    const currentAssigned = linkedIds(selectedCampaign, 'Assigned Stakeholders');
    if (currentAssigned.includes(stakeholderId)) return;
    setAddingContactId(stakeholderId);
    const newAssigned = [...currentAssigned, stakeholderId];
    if (onUpdateRecord) onUpdateRecord('campaigns', selectedCampaign.id, { 'Assigned Stakeholders': newAssigned });
    try {
      const a = api || new AirtableAPI();
      await a.updateRecord(TABLE_IDS.campaigns, selectedCampaign.id, { 'Assigned Stakeholders': newAssigned });
      // NOTE: do NOT update stakeholder's 'Campaign' field — that field is the reverse link
      // of 'Stakeholders Reached' and would mark the contact as reached immediately.
    } catch (e) { console.error('Add contact to campaign failed:', e); }
    setAddingContactId(null);
  };

  const addBulkContactsToCampaign = async () => {
    if (!selectedCampaign || selectedToAdd.size === 0) return;
    setBulkAdding(true);
    const currentAssigned = linkedIds(selectedCampaign, 'Assigned Stakeholders');
    const toAdd = [...selectedToAdd].filter(id => !currentAssigned.includes(id));
    if (toAdd.length === 0) { setBulkAdding(false); setSelectedToAdd(new Set()); return; }
    const newAssigned = [...currentAssigned, ...toAdd];
    if (onUpdateRecord) onUpdateRecord('campaigns', selectedCampaign.id, { 'Assigned Stakeholders': newAssigned });
    try {
      const a = api || new AirtableAPI();
      // Update campaign's Assigned Stakeholders only — do NOT touch stakeholder's 'Campaign' field
      // because that is the reverse link of 'Stakeholders Reached' and would mark them as reached.
      await a.updateRecord(TABLE_IDS.campaigns, selectedCampaign.id, { 'Assigned Stakeholders': newAssigned });
    } catch (e) { console.error('Bulk add contacts failed:', e); window.__oikeToast('Some contacts may not have been added — check your connection and try again.', 'error'); }
    setSelectedToAdd(new Set());
    setShowAddContacts(false);
    setAddContactsSearch('');
    setBulkAdding(false);
  };

  const removeContactFromCampaign = async (stakeholderId) => {
    if (!selectedCampaign) return;
    if (!window.confirm('Remove this contact from the campaign? (Their outreach history stays intact.)')) return;
    setAddingContactId(stakeholderId);
    const currentAssigned = linkedIds(selectedCampaign, 'Assigned Stakeholders');
    const currentReached = linkedIds(selectedCampaign, 'Stakeholders Reached');
    const newAssigned = currentAssigned.filter(id => id !== stakeholderId);
    const newReached = currentReached.filter(id => id !== stakeholderId);
    if (onUpdateRecord) onUpdateRecord('campaigns', selectedCampaign.id, {
      'Assigned Stakeholders': newAssigned,
      'Stakeholders Reached': newReached,
    });
    try {
      const a = api || new AirtableAPI();
      await a.updateRecord(TABLE_IDS.campaigns, selectedCampaign.id, {
        'Assigned Stakeholders': newAssigned,
        'Stakeholders Reached': newReached,
      });
    } catch (e) { console.error('Remove contact from campaign failed:', e); }
    setAddingContactId(null);
  };

  const parseTplContent = (html) => {
    try {
      const m = html.match(/<!--oike-content:(\{[\s\S]*?\})-->/);
      return m ? JSON.parse(m[1]) : null;
    } catch { return null; }
  };

  // Sync email template when campaign changes
  useEffect(() => {
    if (selectedCampaign) {
      const savedHtml = F(selectedCampaign, 'Email HTML Template') || '';
      const savedContent = parseTplContent(savedHtml);
      setEmailTplContent(savedContent);
      // Re-render with current branding (restores photo/colors stripped at save time)
      setEmailTplHtml(savedContent ? renderCampaignEmail(savedContent, selectedCampaign, tplLanguage) : savedHtml);
    }
    setEmailTplDirty(false);
    setEditingTemplate(false);
    setTplOpen(false);
  }, [selectedId]);

  // ── Render campaign email HTML from content sections ──
  const renderCampaignEmail = (S, camp, lang = 'en') => {
    const _br         = loadBranding();
    const accentColor = _br.accentColor  || COMPANY_PROFILE.accentColor || '#1F7A70';
    const darkColor   = _br.darkColor    || COMPANY_PROFILE.darkColor   || '#1a1a2e';
    const _rawLogo    = _br.senderLogo   || COMPANY_PROFILE.senderLogo  || '';
    const _rawPhoto   = _br.senderPhoto  || COMPANY_PROFILE.senderPhoto || '';
    // Prefer explicit public URLs for email (Gmail blocks data: URIs)
    const senderLogo  = _br.senderLogoUrl  || (_rawLogo.startsWith('data:')  ? '' : _rawLogo);
    const senderPhoto = _br.senderPhotoUrl || (_rawPhoto.startsWith('data:') ? '' : _rawPhoto);
    const senderTitle = _br.senderTitle  || COMPANY_PROFILE.senderTitle || '';
    const senderName  = _br.senderName   || COMPANY_PROFILE.senderName  || 'Ale';
    const senderCo    = COMPANY_PROFILE.companyName || 'Oike';
    const senderEmail = _br.senderEmail  || CURRENT_USER?.email || '';
    const calendarLink= _br.calendarLink || '';
    const assetUrl    = F(camp,'Asset URL') || '';
    const campaignName= F(camp,'Name') || '';
    const campaignType= F(camp,'Type') || '';
    const ctaLink     = assetUrl || calendarLink || `mailto:${senderEmail}`;
    const escape = t => String(t||'').replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;').replace(/"/g,'&quot;');
    const nl2br  = t => String(t||'').replace(/\n/g,'<br/>');
    const bullets = Array.isArray(S.bullets) ? S.bullets.filter(Boolean) : [];
    const eventRec = S.eventId ? (events || []).find(e => e.id === S.eventId) : null;
    const evName   = eventRec ? F(eventRec, 'Event Name') : '';
    const evDate   = eventRec?.fields?.['Starting'] ? new Date(eventRec.fields['Starting']).toLocaleDateString('es-AR', { weekday:'long', day:'numeric', month:'long', year:'numeric' }) : '';
    const evUrl    = eventRec ? (F(eventRec, 'URL') || '') : '';
    const evDesc   = eventRec ? (F(eventRec, 'Aditional context') || '').slice(0,180) : '';
    const contentMeta = `<!--oike-content:${JSON.stringify(S)}-->`;
    const i18nDefaults = lang === 'es' ? {
      closingLine1: 'Si algo de esto resuena con lo que está trabajando {{first_name}}, o querés profundizar en algún punto, respondé este mail y lo charlamos.',
      closingLine2: 'Con gusto comparto más contexto o datos específicos para tu industria.',
      replyBtn: 'Responder →',
      calBtn: '📅 Agendemos 15 min',
    } : lang === 'pt' ? {
      closingLine1: 'Se algo aqui ressoa com o que {{first_name}} está trabalhando, ou quiser aprofundar algum ponto, responda este e-mail e conversamos.',
      closingLine2: 'Fico feliz em compartilhar mais contexto ou dados específicos para o seu setor.',
      replyBtn: 'Responder →',
      calBtn: '📅 Agendar 15 min',
    } : {
      closingLine1: 'If any of this resonates with what {{first_name}} is working on, or you\'d like to dig deeper into any of these points, just reply to this email.',
      closingLine2: 'Happy to share more context or data specific to your industry.',
      replyBtn: 'Reply →',
      calBtn: '📅 Book 15 min',
    };
    const i18n = {
      closingLine1: S.closingLine1 || i18nDefaults.closingLine1,
      closingLine2: S.closingLine2 || i18nDefaults.closingLine2,
      replyBtn: S.replyBtn || i18nDefaults.replyBtn,
      calBtn: S.calBtn || i18nDefaults.calBtn,
    };
    return `<!DOCTYPE html PUBLIC "-//W3C//DTD XHTML 1.0 Transitional//EN" "http://www.w3.org/TR/xhtml1/DTD/xhtml1-transitional.dtd">
<html xmlns="http://www.w3.org/1999/xhtml" lang="en">
<head>
<meta http-equiv="Content-Type" content="text/html; charset=UTF-8" />
<meta name="viewport" content="width=device-width,initial-scale=1.0" />
<meta name="x-apple-disable-message-reformatting" />
<title>${escape(campaignName)}</title>
${contentMeta}
<!--[if mso]><style type="text/css">table,td,div,h1,h2,h3,p,a{font-family:Arial,Helvetica,sans-serif !important;}</style><![endif]-->
</head>
<body style="margin:0;padding:0;background:#f3f4f6;font-family:Arial,Helvetica,sans-serif;line-height:1.6;color:${escape(darkColor)};">
<table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" bgcolor="#f3f4f6" style="background:#f3f4f6;">
<tr><td align="center" style="padding:32px 16px;">
<table role="presentation" width="620" cellpadding="0" cellspacing="0" border="0" bgcolor="#ffffff" style="max-width:620px;background:#ffffff;border-radius:16px;border-collapse:separate;">
  <tr><td bgcolor="${escape(darkColor)}" align="center" style="background:${escape(darkColor)};padding:40px 36px 36px;text-align:center;border-bottom:4px solid ${escape(accentColor)};border-radius:16px 16px 0 0;">
${senderLogo ? `<img src="${escape(senderLogo)}" alt="${escape(senderCo)}" width="auto" height="44" style="display:block;max-height:44px;max-width:160px;margin:0 auto 20px;border:0;outline:none;" />` : `<div style="display:inline-block;background:${escape(accentColor)};color:${escape(darkColor)};font-size:18px;font-weight:900;letter-spacing:1px;padding:8px 18px;border-radius:10px;margin:0 auto 20px;font-family:Arial,Helvetica,sans-serif;">${escape(senderCo)}</div>`}
<table role="presentation" cellpadding="0" cellspacing="0" border="0" align="center" style="margin:0 auto 14px;"><tr><td bgcolor="${escape(accentColor)}25" style="background:${escape(accentColor)}25;border:1px solid ${escape(accentColor)}66;border-radius:20px;padding:4px 14px;color:${escape(accentColor)};font-size:10px;font-weight:700;letter-spacing:1.5px;text-transform:uppercase;font-family:Arial,Helvetica,sans-serif;">${escape(campaignType)} &middot; ${escape(senderCo)}</td></tr></table>
<h1 style="margin:0;font-size:28px;color:#ffffff;font-weight:800;line-height:1.2;letter-spacing:-0.5px;font-family:Arial,Helvetica,sans-serif;">${escape(campaignName)}</h1>
${(S.subtitle != null && S.subtitle !== '') ? `<p style="margin:12px 0 0;font-size:13px;color:${escape(accentColor)};letter-spacing:0.5px;font-family:Arial,Helvetica,sans-serif;opacity:0.85;">${escape(S.subtitle)}</p>` : ''}
  </td></tr>
  <tr><td style="padding:36px 36px 28px;">
<table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" style="margin:0 0 28px;border-collapse:separate;"><tr><td bgcolor="${escape(accentColor)}10" style="background:${escape(accentColor)}10;padding:18px 22px;border-left:4px solid ${escape(accentColor)};border-radius:0 10px 10px 0;font-size:15px;line-height:1.6;color:${escape(darkColor)};font-weight:500;font-family:Arial,Helvetica,sans-serif;">${escape(S.hook)}</td></tr></table>
${S.hook ? `<p style="margin:0 0 28px;font-size:15px;color:#374151;line-height:1.65;font-family:Arial,Helvetica,sans-serif;">${nl2br(escape(S.hook))}</p>` : ''}
${S.pain ? `<div style="margin-bottom:28px;"><table role="presentation" cellpadding="0" cellspacing="0" border="0" style="margin-bottom:12px;"><tr><td valign="middle" width="6" style="background:${escape(accentColor)};border-radius:3px;font-size:0;line-height:0;">&nbsp;</td><td valign="middle" style="padding-left:10px;font-size:18px;font-weight:800;color:${escape(darkColor)};font-family:Arial,Helvetica,sans-serif;">${escape(S.painHeading||'')}</td></tr></table><div style="font-size:14px;color:#374151;line-height:1.65;font-family:Arial,Helvetica,sans-serif;">${nl2br(escape(S.pain))}</div></div>` : ''}
${S.value ? `<div style="margin-bottom:24px;"><table role="presentation" cellpadding="0" cellspacing="0" border="0" style="margin-bottom:12px;"><tr><td valign="middle" width="6" style="background:#a78bfa;border-radius:3px;font-size:0;line-height:0;">&nbsp;</td><td valign="middle" style="padding-left:10px;font-size:18px;font-weight:800;color:${escape(darkColor)};font-family:Arial,Helvetica,sans-serif;">${escape(S.valueHeading||'')}</td></tr></table><div style="font-size:14px;color:#374151;line-height:1.65;font-family:Arial,Helvetica,sans-serif;">${nl2br(escape(S.value))}</div></div>` : ''}
${bullets.length ? `<table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" style="margin:0 0 28px;border-collapse:separate;"><tr><td bgcolor="#FAFAFA" style="background:#FAFAFA;padding:18px 20px;border-radius:12px;border:1px solid ${escape(accentColor)}22;"><table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0">${bullets.map((b,i)=>{const c=[accentColor,'#6B5CA5','#2D5F99',accentColor][i%4];const last=i===bullets.length-1;return `<tr><td valign="top" width="38" style="padding:7px 12px 7px 0;${last?'':`border-bottom:1px solid ${escape(c)}15;`}"><table role="presentation" cellpadding="0" cellspacing="0" border="0"><tr><td bgcolor="${escape(c)}20" align="center" valign="middle" width="26" height="26" style="background:${escape(c)}20;color:${escape(c)};font-weight:800;font-size:12px;border-radius:13px;width:26px;height:26px;line-height:26px;text-align:center;font-family:Arial,Helvetica,sans-serif;">${i+1}</td></tr></table></td><td valign="top" style="padding:9px 0 7px;${last?'':`border-bottom:1px solid ${escape(c)}15;`}font-size:13px;color:${escape(darkColor)};line-height:1.5;font-family:Arial,Helvetica,sans-serif;">${escape(b)}</td></tr>`;}).join('')}</table></td></tr></table>` : ''}
${S.socialProof ? `<table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" style="margin:0 0 28px;border-collapse:separate;"><tr><td bgcolor="${escape(accentColor)}12" style="background:${escape(accentColor)}12;border:1px solid ${escape(accentColor)}33;border-radius:12px;padding:18px 22px;">
  <table role="presentation" cellpadding="0" cellspacing="0" border="0" style="margin-bottom:10px;border-collapse:separate;"><tr>
    <td bgcolor="${escape(accentColor)}" align="center" valign="middle" width="28" height="28" style="background:${escape(accentColor)};border-radius:8px;width:28px;height:28px;text-align:center;line-height:28px;font-size:14px;">💡</td>
    <td style="padding-left:10px;font-size:11px;font-weight:800;color:${escape(accentColor)};text-transform:uppercase;letter-spacing:1.5px;font-family:Arial,Helvetica,sans-serif;">${tplLanguage === 'es' ? 'Acción concreta' : tplLanguage === 'pt' ? 'Ação prática' : 'Actionable tip'}</td>
  </tr></table>
  <div style="font-size:14px;color:${escape(darkColor)};line-height:1.65;font-family:Arial,Helvetica,sans-serif;font-weight:500;">${nl2br(escape(S.socialProof))}</div>
</td></tr></table>` : ''}
${evName ? `<table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" style="margin:0 0 24px;border-collapse:separate;">
  <tr><td bgcolor="${escape(accentColor)}08" style="background:${escape(accentColor)}08;border:1px solid ${escape(accentColor)}33;border-radius:12px;padding:20px 22px;">
    <table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0"><tr>
      <td valign="top" style="padding-right:16px;">
        <div style="font-size:10px;font-weight:700;color:${escape(accentColor)};text-transform:uppercase;letter-spacing:1.5px;margin-bottom:6px;font-family:Arial,Helvetica,sans-serif;">&#128197; Evento</div>
        <div style="font-size:17px;font-weight:800;color:${escape(darkColor)};margin-bottom:4px;font-family:Arial,Helvetica,sans-serif;">${escape(evName)}</div>
        ${evDate ? `<div style="font-size:12px;color:#6B7280;margin-bottom:${evDesc?'8':'0'}px;font-family:Arial,Helvetica,sans-serif;">${escape(evDate)}</div>` : ''}
        ${evDesc ? `<div style="font-size:12px;color:#374151;line-height:1.5;font-family:Arial,Helvetica,sans-serif;">${nl2br(escape(evDesc))}</div>` : ''}
      </td>
      ${evUrl ? `<td valign="middle" width="120" align="right" style="white-space:nowrap;">
        <a href="${escape(evUrl)}" style="display:inline-block;padding:10px 18px;background:${escape(accentColor)};color:${escape(darkColor)};text-decoration:none;font-weight:800;font-size:12px;border-radius:8px;font-family:Arial,Helvetica,sans-serif;">Ver evento &rarr;</a>
      </td>` : ''}
    </tr></table>
  </td></tr>
</table>` : ''}
<table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" style="margin-top:28px;border-top:1px solid #E5E7EB;border-collapse:separate;"><tr><td style="padding:28px 0 0;">
  <p style="margin:0 0 6px;font-size:14px;color:#374151;line-height:1.65;font-family:Arial,Helvetica,sans-serif;">${escape(i18n.closingLine1)}</p>
  <p style="margin:0 0 20px;font-size:14px;color:#374151;line-height:1.65;font-family:Arial,Helvetica,sans-serif;">${escape(i18n.closingLine2)}</p>
  <table role="presentation" cellpadding="0" cellspacing="0" border="0" style="margin-bottom:16px;border-collapse:separate;"><tr>
    <td bgcolor="${escape(accentColor)}" style="background:${escape(accentColor)};border-radius:10px;"><a href="mailto:${escape(senderEmail)}" style="display:inline-block;padding:11px 24px;color:${escape(darkColor)};text-decoration:none;font-weight:800;font-size:13px;font-family:Arial,Helvetica,sans-serif;">${escape(i18n.replyBtn)}</a></td>
    ${calendarLink?`<td width="10" style="font-size:0;line-height:0;">&nbsp;</td><td bgcolor="${escape(darkColor)}15" style="background:${escape(darkColor)}15;border-radius:10px;border:1px solid ${escape(accentColor)}44;"><a href="${escape(calendarLink)}" style="display:inline-block;padding:11px 20px;color:${escape(darkColor)};text-decoration:none;font-weight:700;font-size:13px;font-family:Arial,Helvetica,sans-serif;">${escape(i18n.calBtn)}</a></td>`:''}
  </tr></table>
  <table role="presentation" cellpadding="0" cellspacing="0" border="0" style="border-collapse:separate;"><tr>
    ${senderPhoto?`<td width="52" valign="middle" style="padding-right:12px;"><img src="${escape(senderPhoto)}" alt="${escape(senderName)}" width="44" height="44" style="display:block;width:44px;height:44px;border-radius:50%;border:2px solid ${escape(accentColor)};outline:none;" /></td>`:''}
    <td valign="middle" style="font-family:Arial,Helvetica,sans-serif;">
      <div style="font-size:13px;font-weight:700;color:${escape(darkColor)};">${escape(senderName)}</div>
      ${senderTitle?`<div style="font-size:11px;color:#6B7280;margin-top:1px;">${escape(senderTitle)}</div>`:''}
      ${senderEmail?`<div style="font-size:11px;color:${escape(accentColor)};margin-top:2px;">${escape(senderEmail)}</div>`:''}
    </td>
  </tr></table>
</td></tr></table>
  </td></tr>
  <tr><td bgcolor="${escape(darkColor)}" align="center" style="background:${escape(darkColor)};padding:20px 36px;text-align:center;border-radius:0 0 16px 16px;">
<div style="font-size:12px;color:#D1D5DB;font-family:Arial,Helvetica,sans-serif;"><strong style="color:#ffffff;">${escape(senderName)}</strong>${senderEmail?` &middot; <a href="mailto:${escape(senderEmail)}" style="color:${escape(accentColor)};text-decoration:none;">${escape(senderEmail)}</a>`:''}</div>
<div style="margin-top:6px;font-size:10px;color:#9CA3AF;letter-spacing:1px;font-family:Arial,Helvetica,sans-serif;">Powered by <a href="https://oike.app" style="color:${escape(accentColor)};text-decoration:none;font-weight:700;">OIKE</a> &middot; SALES INTELLIGENCE</div>
  </td></tr>
</table></td></tr></table>
</body></html>`;
  };

  // ── Email HTML Template (white paper style, landing-page structure) ──
  const generateEmailTemplate = async () => {
    if (!selectedCampaign) return;
    setGeneratingTpl(true);
    try {
      const campaignName = F(selectedCampaign,'Name') || '';
      const campaignType = F(selectedCampaign,'Type') || '';
      const template     = F(selectedCampaign,'Message Template') || '';
      const context      = F(selectedCampaign,'Context') || '';
      const aiSummary    = F(selectedCampaign,'AI Summary') || '';
      const assetUrl     = F(selectedCampaign,'Asset URL') || '';
      const senderName   = COMPANY_PROFILE.senderName || 'Ale';
      const senderCo     = COMPANY_PROFILE.companyName || 'Oike';
      const senderEmail  = CURRENT_USER?.email || '';
      const companyServices = COMPANY_PROFILE.services || '';
      const companyDesc  = COMPANY_PROFILE.description || COMPANY_PROFILE.companyDescription || '';

      // Aggregate pain intel from assigned contacts (across industries)
      const allContacts = detailContacts.slice(0, 30);
      const pains = allContacts.map(s => F(s,'Pain Points (Generated)') || F(s,'Pain points') || '').filter(Boolean);
      const roles = [...new Set(allContacts.map(s => F(s,'Role') || '').filter(Boolean))].slice(0, 8);
      const industries = [...new Set(allContacts.map(s => { const a = accounts.find(ac => linkedIds(s,'Account').includes(ac.id)); return a ? F(a,'Industry') : ''; }).filter(Boolean))];
      const painSummary = pains.slice(0, 10).join('\n- ').slice(0, 1200);
      const industrySummary = industries.slice(0, 6).join(', ') || 'various industries';

      // Step 1: generate content sections
      const sectionsPrompt = `You are a B2B industry analyst writing a substantive thought leadership email — NOT a salesperson. Your goal is to deliver real value: specific insights, data points, and observations that make the reader think "this person understands my world." Never pitch, never sell, never say "our solution" or "we offer". Write like a trusted advisor sharing what they're seeing across the industry.

Return a JSON object with these exact keys (all strings):
{
  "hook": "2-3 sentences. A sharp, specific observation about what's happening in the target industry RIGHT NOW. Reference real dynamics, tensions, or shifts. Surprising and credible — not generic.",
  "painHeading": "4-6 word heading naming the core tension as an observation. E.g. 'Why alignment breaks down at scale' or 'The gap between strategy and execution'",
  "pain": "3-4 sentences. Describe the challenge in depth — the WHY behind it, the systemic reasons it happens, the cost of ignoring it. Analytical and empathetic, not blaming.",
  "valueHeading": "4-6 word heading for the insight/shift section. E.g. 'What leading teams are doing differently' or 'The move that changes the dynamic'",
  "value": "3-4 sentences. Share specific, concrete approaches that work — what high-performing teams do differently, what patterns lead to better outcomes. Can reference ${senderCo}'s perspective naturally but NOT as a pitch.",
  "bullets": ["Specific, actionable insight or data point — concrete, under 18 words", "insight 2", "insight 3", "insight 4", "insight 5"],
  "socialProof": "1-2 sentences. A concrete, actionable tip the reader can apply immediately — something specific they can do this week to address the challenge described. Not a quote, not social proof. A real recommendation."
}

LANGUAGE: Write ALL content in ${tplLanguage === 'es' ? 'Spanish (Latin American)' : tplLanguage === 'pt' ? 'Brazilian Portuguese' : 'English'}.
CAMPAIGN: "${campaignName}" (${campaignType})
SENDER: ${senderName} — ${senderCo}${companyServices ? ` (${companyServices})` : ''}${companyDesc ? `\nCOMPANY CONTEXT: ${companyDesc.slice(0,400)}` : ''}
TARGET INDUSTRIES: ${industrySummary}
TARGET ROLES: ${roles.join(', ') || 'various'}
PAIN INTEL from ${pains.length} contacts:\n- ${painSummary}
${context ? `\nCAMPAIGN CONTEXT (use this heavily):\n${context.slice(0,1500)}\n` : ''}${aiSummary ? `\nSTRATEGIC BRIEF (use this heavily):\n${aiSummary.slice(0,1200)}\n` : ''}${template ? `\nMESSAGING ANGLE:\n${template.slice(0,600)}\n` : ''}

Be specific. Use the campaign context and strategic brief as the primary source of content. Return ONLY the JSON. No markdown fences.`;

      const sectionsRaw = await callOpenAI({ prompt: sectionsPrompt, temperature: 0.5, max_tokens: 1600 });
      let sections = {};
      try { sections = JSON.parse(sectionsRaw.replace(/^```json\n?|```$/g,'')); } catch { sections = {}; }

      const langClosing = tplLanguage === 'es'
        ? { line1: 'Si algo de esto resuena con los desafíos actuales de tu equipo, respondé este mail y lo charlamos.', line2: 'Con gusto comparto más contexto o datos específicos para tu industria.', reply: 'Responder →', cal: '📅 Agendemos 15 min' }
        : tplLanguage === 'pt'
        ? { line1: 'Se algo aqui ressoa com os desafios atuais do seu time, responda este e-mail e conversamos.', line2: 'Fico feliz em compartilhar mais contexto ou dados específicos para o seu setor.', reply: 'Responder →', cal: '📅 Agendar 15 min' }
        : { line1: 'If any of this resonates with your team\'s current challenges, just reply to this email and let\'s dig in.', line2: 'Happy to share more context or industry-specific data.', reply: 'Reply →', cal: '📅 Book 15 min' };

      const S = {
        subtitle: '',
        hook: sections.hook || '',
        painHeading: sections.painHeading || (tplLanguage === 'es' ? 'El desafío más común' : tplLanguage === 'pt' ? 'O desafio mais comum' : 'The challenge most teams face'),
        pain: sections.pain || '',
        valueHeading: sections.valueHeading || (tplLanguage === 'es' ? 'Lo que está funcionando en 2025' : tplLanguage === 'pt' ? 'O que está funcionando em 2025' : 'What\'s working in 2025'),
        value: sections.value || '',
        bullets: Array.isArray(sections.bullets) ? sections.bullets.filter(Boolean) : [],
        socialProof: sections.socialProof || '',
        closingLine1: langClosing.line1,
        closingLine2: langClosing.line2,
        replyBtn: langClosing.reply,
        calBtn: langClosing.cal,
      };

      // Step 2: render HTML via shared function
      const html = renderCampaignEmail(S, selectedCampaign, tplLanguage);
      setEmailTplContent(S);
      setEmailTplHtml(html);
      setEmailTplDirty(true);
      setPreviewTpl(true);
    } catch(e) {
      window.__oikeToast('Failed to generate template: ' + e.message, 'error');
    }
    setGeneratingTpl(false);
  };

  const saveEmailTemplate = async () => {
    if (!selectedCampaign) return;
    setSavingTpl(true);
    try {
      const a = api || new AirtableAPI();
      // Strip base64-encoded images before saving (they can exceed Airtable's 100k char limit)
      const htmlToSave = emailTplHtml.replace(/src="data:[^"]{100,}"/g, 'src=""');
      await a.updateRecord(TABLE_IDS.campaigns, selectedCampaign.id, { 'Email HTML Template': htmlToSave });
      if (onUpdateRecord) onUpdateRecord('campaigns', selectedCampaign.id, { 'Email HTML Template': htmlToSave });
      setEmailTplDirty(false);
    } catch(e) { window.__oikeToast('Failed to save template: ' + e.message, 'error'); }
    setSavingTpl(false);
  };

  // Generate a personalized opener (1-2 sentences) for a contact using the HTML template flow
  const generateContactOpener = async (s) => {
    const sName = `${F(s,'Name')||''} ${F(s,'Last name')||''}`.trim();
    const firstName = F(s,'Name') || sName;
    const role = F(s,'Role') || '';
    const pain = (F(s,'Pain Points (Generated)') || F(s,'Pain points') || '').slice(0,250);
    const linkedinNews = (F(s,'LinkedIn News (Generated)') || F(s,'Linkedin lates news') || '').slice(0,150);
    const accId = linkedIds(s,'Account')[0];
    const acc = accounts.find(a => a.id === accId);
    const accName = acc ? F(acc,'Account Name') : '';
    const industry = acc ? F(acc,'Industry') : '';
    const accNews = acc ? (F(acc,'Recent News')||'').slice(0,120) : '';
    const campaignName = F(selectedCampaign,'Name') || '';
    const aiSummary = (F(selectedCampaign,'AI Summary') || '').slice(0,300);
    const context = (F(selectedCampaign,'Context') || '').slice(0,300);

    const langLabel = tplLanguage === 'es' ? 'Spanish (Latin American)' : tplLanguage === 'pt' ? 'Brazilian Portuguese' : 'English';
    const prompt = `Write 1-2 sentences in ${langLabel} that open a B2B email to ${firstName} at ${accName || 'their company'}. The sentence must:
- Reference something specific about them or their company (use pain, LinkedIn news, or company news if available)
- Connect naturally to the campaign angle below
- Sound like a human wrote it, not a template
- Be under 40 words total
- NOT start with "I", "We", "Hope", or "Following up"

CONTACT: ${firstName} | ${role} | ${accName}${industry ? ` (${industry})` : ''}
${pain ? `Pain: ${pain}` : ''}${linkedinNews ? `\nLinkedIn: ${linkedinNews}` : ''}${accNews ? `\nNews: ${accNews}` : ''}
CAMPAIGN ANGLE: ${aiSummary || context || campaignName}

Return ONLY the 1-2 sentences in ${langLabel}. No greeting, no subject line.`;

    const opener = await callOpenAI({ prompt, temperature: 0.8, max_tokens: 80, method: 'messages' });
    return opener.trim();
  };

  // Available contacts to add (not yet assigned, filtered by search)
  const availableToAdd = useMemo(() => {
    if (!selectedCampaign || !showAddContacts) return [];
    const assigned = new Set(assignedIds);
    const q = addContactsSearch.toLowerCase().trim();
    let pool = stakeholders.filter(s => !assigned.has(s.id));
    if (q) {
      pool = pool.filter(s => {
        const full = `${F(s,'Name')||''} ${F(s,'Last name')||''} ${F(s,'Role')||''}`.toLowerCase();
        const accId = linkedIds(s,'Account')[0];
        const acc = accId ? accounts.find(a => a.id === accId) : null;
        const accName = acc ? (F(acc,'Account Name')||'').toLowerCase() : '';
        return full.includes(q) || accName.includes(q);
      });
    }
    return pool.slice(0, 50); // cap to avoid huge lists
  }, [selectedCampaign, showAddContacts, addContactsSearch, assignedIds, stakeholders, accounts]);

  // ── Form helpers ──
  const openCreate = () => {
    setEditingCampaign(null);
    setForm({ name:'', type:'White Paper', status:'Draft', messageTemplate:'', assetUrl:'', startDate:'', goal:'', context:'', assignedIds: [] });
    setShowForm(true);
  };

  const openEdit = (c) => {
    setEditingCampaign(c);
    setForm({
      name: F(c,'Name') || '',
      type: F(c,'Type') || 'White Paper',
      status: F(c,'Status') || 'Draft',
      messageTemplate: F(c,'Message Template') || '',
      assetUrl: F(c,'Asset URL') || '',
      startDate: c.fields?.['Start Date'] || '',
      goal: c.fields?.['Goal'] ? String(c.fields['Goal']) : '',
      context: F(c,'Context') || '',
      assignedIds: linkedIds(c, 'Assigned Stakeholders'),
    });
    setShowForm(true);
  };

  const saveCampaign = async () => {
    if (!form.name.trim()) { window.__oikeToast('Campaign name is required', 'warning'); return; }
    setSaving(true);
    try {
      const fields = {
        'Name': form.name.trim(),
        'Type': form.type,
        'Status': form.status,
        'Message Template': form.messageTemplate,
        ...(form.assetUrl ? { 'Asset URL': form.assetUrl } : {}),
        ...(form.startDate ? { 'Start Date': form.startDate } : {}),
        ...(form.goal ? { 'Goal': parseInt(form.goal) || 0 } : {}),
        ...(form.context ? { 'Context': form.context } : {}),
        ...(form.assignedIds && form.assignedIds.length > 0 ? { 'Assigned Stakeholders': form.assignedIds } : {}),
      };
      if (editingCampaign) {
        await api.updateRecord(TABLE_IDS.campaigns, editingCampaign.id, fields);
        if (onUpdateRecord) onUpdateRecord('campaigns', editingCampaign.id, fields);
      } else {
        const created = await api.createRecord(TABLE_IDS.campaigns, fields);
        if (onAddRecord) onAddRecord('campaigns', created?.fields || fields, created?.id);
        // After creating, navigate directly to the new campaign's detail view
        if (created?.id) setSelectedId(created.id);
      }
      setShowForm(false);
      if (onLogActivity) onLogActivity();
    } catch(e) {
      window.__oikeToast('Error saving campaign: ' + e.message, 'error');
    }
    setSaving(false);
  };

  // ── Generate message ──
  const generateMsg = async (s) => {
    setInvitePreview({ id: s.id, msg: '', generating: true });
    const sName = `${F(s,'Name')||''} ${F(s,'Last name')||''}`.trim();
    const role = F(s,'Role') || '';
    const influence = F(s,'Level of Influence') || '';
    const pain = (F(s,'Pain Points (Generated)') || F(s,'Pain points') || '').slice(0,300);
    const linkedinNews = (F(s,'LinkedIn News (Generated)') || F(s,'Linkedin lates news') || '').slice(0,200);
    const accId = linkedIds(s,'Account')[0];
    const acc = accounts.find(a => a.id === accId);
    const accName = acc ? F(acc,'Account Name') : '';
    const industry = acc ? F(acc,'Industry') : '';
    const accNews = acc ? (F(acc,'Recent News')||'').slice(0,150) : '';
    const sOut = outreach
      .filter(o => linkedIds(o,'Stakeholder').includes(s.id))
      .sort((a,b) => new Date(b.fields?.['Date']||0) - new Date(a.fields?.['Date']||0))
      .slice(0,3)
      .map(o => `[${F(o,'Channel')||'?'} · ${o.fields?.['Date'] ? new Date(o.fields['Date']).toLocaleDateString('en-US',{month:'short',day:'numeric'}) : '?'}] ${aiMessageOf(o).slice(0,120)}`)
      .join('\n');
    const campaignName = F(selectedCampaign,'Name') || '';
    const campaignType = F(selectedCampaign,'Type') || '';
    const template = F(selectedCampaign,'Message Template') || '';
    const assetUrl = F(selectedCampaign,'Asset URL') || '';
    const campaignContext = F(selectedCampaign,'Context') || '';
    const campaignAiSummary = F(selectedCampaign,'AI Summary') || '';

    const prompt = `B2B sales rep running a ${campaignType} campaign. Write ONE short personalized message. Max 3 sentences + subject if email.

CONTACT: ${sName} | ${role}${influence ? ` (${influence})` : ''} | ${accName}${industry ? ` — ${industry}` : ''}
${pain ? `Pain: ${pain}` : ''}${linkedinNews ? `\nLinkedIn: ${linkedinNews}` : ''}${accNews ? `\nCompany news: ${accNews}` : ''}
History: ${sOut || 'First contact'}

CAMPAIGN: "${campaignName}" (${campaignType})
${campaignContext ? `\nCAMPAIGN CONTEXT (author-written — treat as strategic ground truth):\n${campaignContext.slice(0, 800)}\n` : ''}
${campaignAiSummary ? `\nCAMPAIGN STRATEGIC BRIEF (AI-generated from context):\n${campaignAiSummary.slice(0, 800)}\n` : ''}
${template ? `\nReference angle (personalize — DO NOT copy verbatim, rewrite for this specific person):\n"${template.slice(0,400)}"` : ''}
${assetUrl ? `Asset/resource to reference: ${assetUrl}` : ''}

MISSION: Connect this ${campaignType} campaign to ${sName}'s specific context at ${accName}. Align the message with the CAMPAIGN CONTEXT and STRATEGIC BRIEF above — those define the angle, pain, and outcome. Be direct, specific, and relevant to their role.
BANNED: "following up"/"checking in"/"hope this finds you"/"touching base"/brackets/placeholders.
Sender: ${COMPANY_PROFILE.senderName||'Ale'}, ${COMPANY_PROFILE.companyName||'Oike'}
If email: line 1 = "Subject: [subject]", blank line, body. Output ONLY the message.`;

    try {
      const msg = await callOpenAI({ prompt, temperature: 0.75, max_tokens: 250, method: 'messages' });
      setInvitePreview({ id: s.id, msg: msg.trim(), generating: false });
    } catch(e) {
      console.error('Campaign generateMsg failed:', e);
      const fallback = `Hi ${sName}, I wanted to share something that might be relevant to your work at ${accName}${industry ? ` in the ${industry} space` : ''}. ${template ? template.slice(0,120) + '...' : 'Would love to connect and share more.'} Can we set up a quick call?`;
      setInvitePreview({ id: s.id, msg: fallback, generating: false });
    }
  };

  // ── Send message ──
  const sendMsg = (_s, channel) => {
    if (!invitePreview?.msg) return;
    const s = stakeholders.find(x => x.id === invitePreview.id) || _s;
    const msg = invitePreview.msg;
    const email = F(s,'Email')||'';
    const phone = F(s,'Phone number')||'';
    const linkedin = F(s,'LinkedIn')||'';
    let subject = '', body = msg;
    if (channel === 'Email') {
      const lines = body.split('\n');
      const si = lines.findIndex(l => /^subject:/i.test(l.trim()));
      if (si !== -1) { subject = lines[si].replace(/^subject:\s*/i,'').trim(); body = lines.slice(si+1).join('\n').trim(); }
      else { subject = `${F(selectedCampaign,'Name')||'Campaign'} — ${F(s,'Name')||''}`; }
    }
    // Open channel synchronously (before any await)
    if (channel==='WhatsApp'&&phone) window.open(`https://wa.me/${String(phone).replace(/[^0-9+]/g,'')}?text=${encodeURIComponent(msg)}`,'_blank');
    else if (channel==='Email'&&email) window.open(`https://mail.google.com/mail/?view=cm&to=${encodeURIComponent(email)}&su=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`,'_blank');
    else if (channel==='LinkedIn'&&linkedin) { navigator.clipboard.writeText(msg).catch(()=>{}); window.open(linkedin,'_blank'); }

    // Log outreach + update Stakeholders Reached
    const companyIds = linkedIds(s,'Account');
    const sName = `${F(s,'Name')||''} ${F(s,'Last name')||''}`.trim();
    const campaignName = F(selectedCampaign,'Name')||'';
    const a = api || new AirtableAPI();
    a.createRecord(TABLE_IDS.outreach, {
      'Activity Name': `Campaign "${campaignName}": ${sName} — ${new Date().toLocaleDateString('en-US')}`,
      'Account': companyIds, 'Stakeholder': [s.id],
      'Channel': channel, 'Date': new Date().toISOString(),
      'Status': 'Sent', 'Message': msg,
      'Notes': `Campaign: ${campaignName} (${F(selectedCampaign,'Type')||''})`,
      'Logged By': CURRENT_USER?.name || '',
    }).then(async () => {
      const currentReached = linkedIds(selectedCampaign, 'Stakeholders Reached');
      const currentAssigned = linkedIds(selectedCampaign, 'Assigned Stakeholders');
      await a.updateRecord(TABLE_IDS.campaigns, selectedCampaign.id, {
        'Stakeholders Reached': [...new Set([...currentReached, s.id])],
        // Also ensure the contact is in Assigned (in case they were added ad-hoc)
        'Assigned Stakeholders': [...new Set([...currentAssigned, s.id])],
      }).catch(e => console.error('Campaign reached update failed:', e));
      await activateAccountIfNeeded(a, companyIds, data.accounts);
      await updateStakeholderStatus(a, s.id, 'Contacted', data.stakeholders);
      if (onLogActivity) onLogActivity();
    }).catch(e => console.error('Campaign send log failed:', e));

    setInvitePreview(null);
  };

  // ── Delete campaign ──
  const deleteCampaign = async (c) => {
    const name = F(c, 'Name') || 'this campaign';
    if (!window.confirm(`Delete "${name}"? This cannot be undone.`)) return;
    try {
      const a = api || new AirtableAPI();
      await a.deleteRecord(TABLE_IDS.campaigns, c.id);
      if (onDeleteRecord) onDeleteRecord('campaigns', c.id);
      if (selectedId === c.id) { setSelectedId(null); setInvitePreview(null); }
      window.__oikeToast(`"${name}" deleted`, 'success');
    } catch(e) {
      window.__oikeToast('Failed to delete campaign: ' + e.message, 'error');
    }
  };

  const inputSt = { background:'var(--globant-darker)', border:'1px solid var(--globant-border)', borderRadius:6, color:'var(--globant-text)', padding:'8px 10px', fontSize:13, width:'100%' };

  // ── DETAIL VIEW ──
  if (selectedCampaign) {
    const reached = reachedIds.length;
    const goal = selectedCampaign.fields?.['Goal'] || 0;
    const campaignType = F(selectedCampaign,'Type') || '';
    const status = F(selectedCampaign,'Status') || '';
    const template = F(selectedCampaign,'Message Template') || '';
    const assetUrl = F(selectedCampaign,'Asset URL') || '';

    return (
      <div>
        {/* Back + header */}
        <div className="page-header" style={{ marginBottom:12 }}>
          <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:4 }}>
            <button className="action-btn btn-ghost" style={{ fontSize:11 }} onClick={() => { setSelectedId(null); setInvitePreview(null); setFilterIndustry(''); setFilterRole(''); setFilterSearch(''); }}>← Back</button>
            <span style={{ fontSize:11, color:'var(--globant-muted)' }}>Campaigns</span>
          </div>
          <div style={{ display:'flex', alignItems:'center', gap:10, flexWrap:'wrap' }}>
            <span style={{ fontSize:18 }}>{TYPE_ICONS[campaignType]||'📣'}</span>
            <h1 style={{ margin:0 }}>{F(selectedCampaign,'Name')}</h1>
            <span style={{ fontSize:11, padding:'3px 10px', borderRadius:20, background:`${STATUS_COLORS[status]||'#666'}20`, color:STATUS_COLORS[status]||'var(--globant-muted)', fontWeight:700, border:`1px solid ${STATUS_COLORS[status]||'var(--globant-border)'}40` }}>{status}</span>
            <div style={{ display:'flex', gap:6, marginLeft:'auto' }}>
              <button className="action-btn btn-ghost" style={{ fontSize:11 }} onClick={() => openEdit(selectedCampaign)}>✏️ Edit</button>
              <button className="action-btn btn-ghost" style={{ fontSize:11, color:'#B4362C', border:'1px solid rgba(239,68,68,0.3)' }} onClick={() => deleteCampaign(selectedCampaign)}>🗑️ Delete</button>
            </div>
          </div>
          <div style={{ display:'flex', gap:16, marginTop:6, flexWrap:'wrap' }}>
            <span style={{ fontSize:12, color:'var(--globant-muted)' }}>Type: <strong style={{ color:'var(--globant-text)' }}>{campaignType}</strong></span>
            {selectedCampaign.fields?.['Start Date'] && <span style={{ fontSize:12, color:'var(--globant-muted)' }}>Start: <strong style={{ color:'var(--globant-text)' }}>{formatDate(selectedCampaign.fields['Start Date'])}</strong></span>}
            <span style={{ fontSize:12, color:'var(--globant-muted)' }}>Reached: <strong style={{ color:'#1E7B4F' }}>{reached}</strong>{goal>0 ? ` / ${goal} goal` : ''}</span>
            {assetUrl && <a href={assetUrl} target="_blank" rel="noopener noreferrer" style={{ fontSize:12, color:'var(--globant-green)' }}>🔗 View Asset</a>}
          </div>
          {F(selectedCampaign, 'Last Run') && (
            <div style={{ fontSize: 11, color: 'var(--globant-muted)', marginTop: 6 }}>
              Last run: {new Date(F(selectedCampaign, 'Last Run')).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
              {F(selectedCampaign, 'Last Run Result') && <span style={{ marginLeft: 8, color: 'var(--globant-green)' }}>· {F(selectedCampaign, 'Last Run Result')}</span>}
            </div>
          )}
        </div>

        {/* Message Template — inline editable */}
        <div className="card" style={{ marginBottom:14, borderLeft:'3px solid var(--globant-green)', padding:'10px 14px' }}>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:6 }}>
            <div style={{ fontSize:11, fontWeight:700, color:'var(--globant-green)', textTransform:'uppercase', letterSpacing:1 }}>✍️ Message Template / Reference Angle</div>
            <div style={{ display:'flex', gap:6, alignItems:'center' }}>
              {!editingTemplate ? (
                <>
                  {template.length > 200 && (
                    <button onClick={() => setTplExpanded(x=>!x)} style={{ background:'none', border:'none', color:'var(--globant-green)', cursor:'pointer', fontSize:10, padding:0, fontWeight:600 }}>
                      {tplExpanded ? '▲ Less' : '▼ More'}
                    </button>
                  )}
                  <button className="action-btn btn-ghost" style={{ fontSize:10 }}
                    onClick={() => { setTemplateDraft(template); setEditingTemplate(true); setTplExpanded(true); }}>
                    {template ? '✏️ Edit' : '➕ Add Template'}
                  </button>
                </>
              ) : (
                <>
                  <button className="action-btn btn-primary" style={{ fontSize:10 }} onClick={saveCampaignTemplate} disabled={savingTemplate}>
                    {savingTemplate ? '⏳' : '💾 Save'}
                  </button>
                  <button className="action-btn btn-ghost" style={{ fontSize:10 }} onClick={() => setEditingTemplate(false)}>Cancel</button>
                </>
              )}
            </div>
          </div>
          {editingTemplate ? (
            <textarea
              className="input-field"
              style={{ width:'100%', minHeight:120, resize:'vertical', fontFamily:'inherit', fontSize:12, lineHeight:1.6 }}
              placeholder={`Write the reference angle / message template the AI will personalize per contact.\n\nTips:\n- Opening hook or observation\n- Main pain you're addressing\n- What you're sharing and why it matters\n- Call to action`}
              value={templateDraft}
              onChange={e => setTemplateDraft(e.target.value)}
            />
          ) : template ? (
            <div style={{ fontSize:12, color:'var(--globant-text)', lineHeight:1.7, whiteSpace:'pre-wrap', overflow:'hidden', maxHeight: tplExpanded || template.length <= 200 ? 'none' : '72px', position:'relative' }}>
              {template}
              {!tplExpanded && template.length > 200 && (
                <div style={{ position:'absolute', bottom:0, left:0, right:0, height:32, background:'linear-gradient(transparent, var(--globant-card))' }} />
              )}
            </div>
          ) : (
            <div style={{ fontSize:12, color:'var(--globant-muted)', fontStyle:'italic' }}>
              No template yet — click "Add Template" to write the reference angle the AI will use to personalize each message.
            </div>
          )}
        </div>

        {/* Campaign Brief — Context + AI Summary merged */}
        <div className="card" style={{ marginBottom: 14, padding: '12px 14px' }}>
          {/* Tab header */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
            <div style={{ display: 'flex', gap: 0, borderBottom: '1px solid var(--globant-border)' }}>
              {[['context','📝 Context'],['ai','🧠 AI Brief']].map(([k,label]) => (
                <button key={k} onClick={() => setBriefTab(k)} style={{ padding:'4px 14px', background:'none', border:'none', borderBottom: briefTab===k ? '2px solid var(--globant-green)' : '2px solid transparent', color: briefTab===k ? 'var(--globant-green)' : 'var(--globant-muted)', fontWeight: briefTab===k ? 700 : 400, fontSize:11, cursor:'pointer' }}>
                  {label}
                </button>
              ))}
            </div>
            {briefTab === 'context' && (
              <div style={{ display: 'flex', gap: 6 }}>
                {!editingContext ? (
                  <button className="action-btn btn-ghost" style={{ fontSize: 10 }}
                    onClick={() => { setContextDraft(F(selectedCampaign, 'Context') || ''); setEditingContext(true); }}>
                    {F(selectedCampaign, 'Context') ? '✏️ Edit' : '➕ Add'}
                  </button>
                ) : (
                  <>
                    <button className="action-btn btn-primary" style={{ fontSize: 10 }} onClick={saveCampaignContext} disabled={savingContext}>
                      {savingContext ? '⏳' : '💾 Save'}
                    </button>
                    <button className="action-btn btn-ghost" style={{ fontSize: 10 }} onClick={() => setEditingContext(false)}>Cancel</button>
                  </>
                )}
                <label style={{ cursor: 'pointer' }}>
                  <input type="file" accept=".csv,.txt,.json,.md,.html,.tsv,.xml,.pdf" style={{ display: 'none' }} onChange={handleCampaignFileUpload} disabled={campaignUploadingFile} />
                  <span className="action-btn btn-ghost" style={{ fontSize: 10, padding: '3px 10px', display: 'inline-block' }}>
                    {campaignUploadingFile ? '⏳ Processing...' : '📎 File'}
                  </span>
                </label>
              </div>
            )}
            {briefTab === 'ai' && (
              <button className="action-btn btn-primary" style={{ fontSize: 10 }}
                onClick={generateCampaignSummary} disabled={generatingSummary}>
                {generatingSummary ? '⏳ Generating...' : F(selectedCampaign, 'AI Summary') ? '🔄 Regenerate' : '✨ Generate'}
              </button>
            )}
          </div>

          {/* Context tab */}
          {briefTab === 'context' && (
            editingContext ? (
              <textarea
                className="input-field"
                style={{ width: '100%', minHeight: 120, resize: 'vertical', fontFamily: 'inherit', fontSize: 12, lineHeight: 1.5 }}
                placeholder={`Write context the AI should know about this campaign:\n- Goals / outcome desired\n- ICP nuances (industries, roles)\n- Pain points this asset addresses\n- Messaging angle / tone\n- Triggers or timing reasons\n- What to avoid mentioning`}
                value={contextDraft}
                onChange={e => setContextDraft(e.target.value)} />
            ) : (
              F(selectedCampaign, 'Context') ? (
                <div style={{ overflow:'hidden', maxHeight: ctxExpanded || (F(selectedCampaign,'Context')||'').length <= 150 ? 'none' : '80px', position:'relative', transition:'max-height 0.2s' }}>
                  <FileNotesRenderer
                    notes={F(selectedCampaign, 'Context')}
                    accentColor="var(--globant-accent)"
                    onUpdateNotes={async (updated) => {
                      const a = api || new AirtableAPI();
                      await a.updateRecord(TABLE_IDS.campaigns, selectedCampaign.id, { 'Context': updated });
                      if (onUpdateRecord) onUpdateRecord('campaigns', selectedCampaign.id, { 'Context': updated });
                      if (onLogActivity) onLogActivity();
                    }}
                  />
                  {!ctxExpanded && (F(selectedCampaign,'Context')||'').length > 150 && (
                    <div style={{ position:'absolute', bottom:0, left:0, right:0, height:32, background:'linear-gradient(transparent, var(--globant-card))' }} />
                  )}
                  {(F(selectedCampaign,'Context')||'').length > 150 && (
                    <button onClick={() => setCtxExpanded(x=>!x)} style={{ background:'none', border:'none', color:'var(--globant-muted)', cursor:'pointer', fontSize:10, marginTop:4 }}>
                      {ctxExpanded ? '▲ Less' : '▼ More'}
                    </button>
                  )}
                </div>
              ) : (
                <div style={{ fontSize: 12, color: 'var(--globant-muted)', fontStyle: 'italic' }}>
                  No context yet — click "Add" to write manually, or "File" to let AI extract key points from a doc.
                </div>
              )
            )
          )}

          {/* AI Brief tab */}
          {briefTab === 'ai' && (
            F(selectedCampaign, 'AI Summary') ? (
              <div style={{ overflow:'hidden', maxHeight: sumExpanded || (F(selectedCampaign,'AI Summary')||'').length <= 150 ? 'none' : '120px', position:'relative', transition:'max-height 0.2s' }}>
              <div style={{ fontSize: 12, color: 'var(--globant-text)', lineHeight: 1.6 }}>
                {(() => {
                  const summaryText = F(selectedCampaign, 'AI Summary');
                  const lines = summaryText.split('\n').filter(l => l.trim());
                  const parseInline = (text) => {
                    const parts = [];
                    const regex = /\*\*(.+?)\*\*/g;
                    let lastIndex = 0, match, key = 0;
                    while ((match = regex.exec(text)) !== null) {
                      if (match.index > lastIndex) parts.push(<span key={key++}>{text.slice(lastIndex, match.index)}</span>);
                      parts.push(<strong key={key++} style={{ fontWeight: 700, color: 'var(--globant-text)' }}>{match[1]}</strong>);
                      lastIndex = match.index + match[0].length;
                    }
                    if (lastIndex < text.length) parts.push(<span key={key++}>{text.slice(lastIndex)}</span>);
                    return parts.length ? parts : text;
                  };
                  return lines.map((line, i) => {
                    if (line.startsWith('### ')) return <h4 key={i} style={{ margin: '8px 0 4px', fontSize: 12, fontWeight: 700, color: 'var(--globant-green)' }}>{parseInline(line.replace('### ', '').replace(/\*\*/g, ''))}</h4>;
                    if (line.startsWith('- ') || line.startsWith('* ')) return <div key={i} style={{ paddingLeft: 12, marginBottom: 3, position: 'relative' }}><span style={{ position: 'absolute', left: 0 }}>•</span>{parseInline(line.slice(2))}</div>;
                    return <p key={i} style={{ margin: '3px 0' }}>{parseInline(line)}</p>;
                  });
                })()}
              </div>
              {!sumExpanded && (F(selectedCampaign,'AI Summary')||'').length > 150 && (
                <>
                  <div style={{ position:'absolute', bottom:0, left:0, right:0, height:32, background:'linear-gradient(transparent, var(--globant-card))' }} />
                  <button onClick={() => setSumExpanded(x=>!x)} style={{ background:'none', border:'none', color:'var(--globant-muted)', cursor:'pointer', fontSize:10, marginTop:4 }}>▼ More</button>
                </>
              )}
              {sumExpanded && (F(selectedCampaign,'AI Summary')||'').length > 150 && (
                <button onClick={() => setSumExpanded(x=>!x)} style={{ background:'none', border:'none', color:'var(--globant-muted)', cursor:'pointer', fontSize:10, marginTop:4 }}>▲ Less</button>
              )}
              </div>
            ) : (
              <div style={{ fontSize: 12, color: 'var(--globant-muted)', fontStyle: 'italic' }}>
                {F(selectedCampaign, 'Context') ? 'Click "Generate" to have AI create a strategic brief for this campaign.' : 'Add context first, then generate the AI brief.'}
              </div>
            )
          )}
        </div>

        {/* Email HTML Template */}
        <div className="card" style={{ marginBottom: 14, borderLeft: '3px solid #2D5F99', padding: '12px 14px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#2D5F99', textTransform: 'uppercase', letterSpacing: 1 }}>📧 Email HTML Template</div>
            <button className="action-btn btn-ghost" style={{ fontSize: 10 }} onClick={() => setShowEmailTpl(!showEmailTpl)}>
              {showEmailTpl ? '▲ Collapse' : (emailTplHtml ? '▼ Template saved' : '▼ Build template')}
            </button>
          </div>
          {showEmailTpl && (
            <div style={{ marginTop: 12 }}>
              <div style={{ display: 'flex', gap: 8, marginBottom: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                <select value={tplLanguage} onChange={e => {
                  const lang = e.target.value;
                  setTplLanguage(lang);
                  if (emailTplContent && selectedCampaign) {
                    setEmailTplHtml(renderCampaignEmail(emailTplContent, selectedCampaign, lang));
                    setEmailTplDirty(true);
                  }
                }} style={{ fontSize: 11, padding: '4px 8px', borderRadius: 6, border: '1px solid rgba(30,42,44,0.15)', background: 'rgba(30,42,44,0.07)', color: '#3E4A47', cursor: 'pointer' }}>
                  <option value="en">🇬🇧 English</option>
                  <option value="es">🇦🇷 Español</option>
                  <option value="pt">🇧🇷 Português</option>
                </select>
                <button className="action-btn btn-primary" style={{ fontSize: 11 }}
                  onClick={generateEmailTemplate} disabled={generatingTpl}>
                  {generatingTpl ? '⏳ Generating...' : emailTplHtml ? '🔄 Regenerate' : '✨ Generate Template'}
                </button>
                {emailTplHtml && (
                  <button className="action-btn btn-ghost" style={{ fontSize: 11 }}
                    onClick={() => setPreviewTpl(!previewTpl)}>
                    {previewTpl ? '✏️ Edit' : '👁️ Preview'}
                  </button>
                )}
                {emailTplDirty && (
                  <button className="action-btn" style={{ fontSize: 11, background: 'rgba(96,165,250,0.15)', color: '#2D5F99', border: '1px solid rgba(96,165,250,0.3)' }}
                    onClick={saveEmailTemplate} disabled={savingTpl}>
                    {savingTpl ? '⏳ Saving...' : '💾 Save'}
                  </button>
                )}
                {emailTplHtml && (
                  <>
                    <button className="action-btn btn-ghost" style={{ fontSize: 11 }} onClick={downloadHtml} title="Download as HTML file">⬇️ HTML</button>
                    <button className="action-btn btn-ghost" style={{ fontSize: 11 }} onClick={downloadPdf} title="Open in new tab and print to PDF">🖨️ PDF</button>
                  </>
                )}
              </div>
              <div style={{ marginBottom: 12 }}>
                <label style={{ fontSize: 10, fontWeight: 700, color: 'var(--globant-muted)', textTransform: 'uppercase', letterSpacing: 0.8, display: 'block', marginBottom: 4 }}>
                  Subject line <span style={{ fontWeight: 400, textTransform: 'none', letterSpacing: 0 }}>— supports <code style={{ fontSize: 10 }}>{'{{first_name}}'}</code> and <code style={{ fontSize: 10 }}>{'{{company}}'}</code></span>
                </label>
                <input
                  className="input-field"
                  style={{ width: '100%', fontSize: 12, padding: '6px 10px' }}
                  placeholder={`${F(selectedCampaign,'Name')||'Campaign'} — for {{first_name}} at {{company}}`}
                  value={emailTplSubject}
                  onChange={e => setEmailTplSubject(e.target.value)}
                />
              </div>

              {emailTplHtml && (
                previewTpl ? (
                  <div style={{ border: '1px solid var(--globant-border)', borderRadius: 6, overflow: 'hidden', background: '#fff' }}>
                    <div style={{ padding: '6px 10px', background: 'var(--globant-darker)', borderBottom: '1px solid var(--globant-border)', fontSize: 10, color: 'var(--globant-muted)' }}>
                      Preview — tokens filled per contact on send
                    </div>
                    <iframe
                      srcDoc={emailTplHtml}
                      style={{ width: '100%', height: 480, border: 'none', display: 'block' }}
                      sandbox="allow-same-origin"
                      title="Email preview"
                    />
                  </div>
                ) : (() => {
                  const C = emailTplContent || {};
                  const updateContent = (key, val) => {
                    const updated = { ...C, [key]: val };
                    setEmailTplContent(updated);
                    setEmailTplHtml(renderCampaignEmail(updated, selectedCampaign, tplLanguage));
                    setEmailTplDirty(true);
                  };
                  const updateBullet = (i, val) => {
                    const bullets = [...(C.bullets || [])];
                    bullets[i] = val;
                    updateContent('bullets', bullets);
                  };
                  const addBullet = () => updateContent('bullets', [...(C.bullets || []), '']);
                  const removeBullet = (i) => {
                    const bullets = (C.bullets || []).filter((_,idx) => idx !== i);
                    updateContent('bullets', bullets);
                  };
                  const fieldStyle = { width: '100%', marginBottom: 10, boxSizing: 'border-box' };
                  const labelStyle = { fontSize: 10, fontWeight: 700, color: 'var(--globant-muted)', textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 3, display: 'block' };
                  return (
                    <div style={{ background: 'var(--globant-darker)', borderRadius: 8, padding: '14px 14px 6px', border: '1px solid var(--globant-border)' }}>
                      <div style={{ fontSize: 10, color: 'var(--globant-muted)', marginBottom: 12, lineHeight: 1.5 }}>
                        ✏️ Edit the content below — the email updates automatically. Optional tokens <code style={{ color: '#2D5F99' }}>{'{{first_name}}'}</code> · <code style={{ color: '#2D5F99' }}>{'{{company}}'}</code> are replaced per contact on send.
                      </div>

                      <label style={labelStyle}>Header subtitle</label>
                      <select className="input-field" style={{ ...fieldStyle, fontSize: 12 }}
                        value={C.subtitle ?? ''}
                        onChange={e => updateContent('subtitle', e.target.value)}>
                        <option value="">No subtitle</option>
                        <option value="{{company}}">Company name — {'{{company}}'}</option>
                        <option value="{{first_name}}">First name — {'{{first_name}}'}</option>
                        <option value="{{first_name}} at {{company}}">Name at Company — {'{{first_name}} at {{company}}'}</option>
                      </select>

                      <label style={labelStyle}>Opening hook (1-2 sentences)</label>
                      <textarea className="input-field" style={{ ...fieldStyle, minHeight: 56, resize: 'vertical', fontSize: 12 }}
                        value={C.hook || ''} onChange={e => updateContent('hook', e.target.value)} />

                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 10 }}>
                        <div>
                          <label style={labelStyle}>Pain section heading</label>
                          <input className="input-field" style={{ width: '100%', fontSize: 12 }}
                            value={C.painHeading || ''} onChange={e => updateContent('painHeading', e.target.value)} />
                        </div>
                        <div>
                          <label style={labelStyle}>Value section heading</label>
                          <input className="input-field" style={{ width: '100%', fontSize: 12 }}
                            value={C.valueHeading || ''} onChange={e => updateContent('valueHeading', e.target.value)} />
                        </div>
                      </div>

                      <label style={labelStyle}>Pain paragraph</label>
                      <textarea className="input-field" style={{ ...fieldStyle, minHeight: 60, resize: 'vertical', fontSize: 12 }}
                        value={C.pain || ''} onChange={e => updateContent('pain', e.target.value)} />

                      <label style={labelStyle}>Value paragraph</label>
                      <textarea className="input-field" style={{ ...fieldStyle, minHeight: 60, resize: 'vertical', fontSize: 12 }}
                        value={C.value || ''} onChange={e => updateContent('value', e.target.value)} />

                      <label style={labelStyle}>Numbered bullets</label>
                      {(C.bullets || []).map((b, i) => (
                        <div key={i} style={{ display: 'flex', gap: 6, marginBottom: 6 }}>
                          <div style={{ minWidth: 22, height: 22, background: 'rgba(91,191,181,0.2)', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 700, color: '#1F7A70', marginTop: 6 }}>{i+1}</div>
                          <input className="input-field" style={{ flex: 1, fontSize: 12 }}
                            value={b} onChange={e => updateBullet(i, e.target.value)} />
                          <button className="action-btn btn-ghost" style={{ fontSize: 10, padding: '2px 8px', color: '#B4362C' }} onClick={() => removeBullet(i)}>✕</button>
                        </div>
                      ))}
                      <button className="action-btn btn-ghost" style={{ fontSize: 10, marginBottom: 10 }} onClick={addBullet}>+ Add bullet</button>

                      <label style={labelStyle}>💡 Actionable tip (optional)</label>
                      <textarea className="input-field" style={{ ...fieldStyle, minHeight: 60, resize: 'vertical', fontSize: 12 }}
                        placeholder="A concrete action or recommendation the reader can apply right now..."
                        value={C.socialProof || ''} onChange={e => updateContent('socialProof', e.target.value)} />

                      <div style={{ borderTop: '1px solid var(--globant-border)', margin: '10px 0 12px', paddingTop: 12 }}>
                        <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--globant-muted)', textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 8 }}>Closing section</div>
                        <label style={labelStyle}>Line 1</label>
                        <textarea className="input-field" style={{ ...fieldStyle, minHeight: 52, resize: 'vertical', fontSize: 12 }}
                          value={C.closingLine1 || ''} onChange={e => updateContent('closingLine1', e.target.value)} />
                        <label style={labelStyle}>Line 2</label>
                        <input className="input-field" style={{ ...fieldStyle, fontSize: 12 }}
                          value={C.closingLine2 || ''} onChange={e => updateContent('closingLine2', e.target.value)} />
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                          <div>
                            <label style={labelStyle}>Reply button</label>
                            <input className="input-field" style={{ width: '100%', fontSize: 12 }}
                              value={C.replyBtn || ''} onChange={e => updateContent('replyBtn', e.target.value)} />
                          </div>
                          <div>
                            <label style={labelStyle}>Calendar button</label>
                            <input className="input-field" style={{ width: '100%', fontSize: 12 }}
                              value={C.calBtn || ''} onChange={e => updateContent('calBtn', e.target.value)} />
                          </div>
                        </div>
                      </div>

                      <label style={labelStyle}>Evento (opcional)</label>
                      <select className="input-field" style={{ ...fieldStyle, fontSize: 12 }}
                        value={C.eventId || ''}
                        onChange={e => updateContent('eventId', e.target.value || null)}>
                        <option value="">— Sin evento —</option>
                        {(events || []).sort((a,b) => (b.fields?.['Starting']||'').localeCompare(a.fields?.['Starting']||'')).map(ev => {
                          const evD = ev.fields?.['Starting'] ? new Date(ev.fields['Starting']).toLocaleDateString('es-AR', { day:'numeric', month:'short' }) : '';
                          return <option key={ev.id} value={ev.id}>{F(ev,'Event Name')}{evD ? ` (${evD})` : ''}</option>;
                        })}
                      </select>
                      {C.eventId && (
                        <div style={{ fontSize: 10, color: 'var(--globant-muted)', marginTop: -6, marginBottom: 10 }}>
                          Aparece como bloque de invitación antes del CTA.
                        </div>
                      )}
                    </div>
                  );
                })()
              )}
            </div>
          )}
        </div>


        {/* Asset / Landing Page URL */}
        <div style={{ marginBottom: 14, padding: '10px 14px', background: 'rgba(30,42,44,0.03)', borderRadius: 10, border: '1px solid var(--globant-border)', display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--globant-muted)', textTransform: 'uppercase', letterSpacing: 0.8, flexShrink: 0 }}>🔗 Asset / Landing URL</span>
          {editingAssetUrl ? (
            <>
              <input
                className="input-field"
                style={{ flex: 1, fontSize: 12, minWidth: 200 }}
                placeholder="https://..."
                value={assetUrlDraft}
                onChange={e => setAssetUrlDraft(e.target.value)}
                autoFocus
                onKeyDown={e => { if (e.key === 'Enter') saveAssetUrl(); if (e.key === 'Escape') setEditingAssetUrl(false); }}
              />
              <button className="action-btn btn-primary" style={{ fontSize: 11 }} onClick={saveAssetUrl} disabled={savingAssetUrl}>{savingAssetUrl ? '⏳' : '💾 Save'}</button>
              <button className="action-btn btn-ghost" style={{ fontSize: 11 }} onClick={() => setEditingAssetUrl(false)}>Cancel</button>
            </>
          ) : (
            <>
              {assetUrl ? (
                <a href={assetUrl} target="_blank" rel="noopener noreferrer" style={{ fontSize: 12, color: 'var(--globant-green)', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{assetUrl}</a>
              ) : (
                <span style={{ fontSize: 12, color: 'var(--globant-muted)', fontStyle: 'italic', flex: 1 }}>No URL set — add one and it'll be appended to all generated messages</span>
              )}
              <button className="action-btn btn-ghost" style={{ fontSize: 11 }} onClick={() => { setAssetUrlDraft(assetUrl); setEditingAssetUrl(true); }}>
                {assetUrl ? '✏️ Edit' : '➕ Add URL'}
              </button>
            </>
          )}
        </div>

        {/* Stats */}
        <div className="kpi-row" style={{ gridTemplateColumns:'repeat(3,1fr)', marginBottom:14 }}>
          <div className="kpi-card">
            <div className="kpi-label">Assigned</div>
            <div className="kpi-value">{assignedIds.length}</div>
          </div>
          <div className="kpi-card">
            <div className="kpi-label">Reached</div>
            <div className="kpi-value" style={{ color:'#1E7B4F' }}>{assignedIds.filter(id=>reachedIds.includes(id)).length}</div>
          </div>
          <div className="kpi-card">
            <div className="kpi-label">Pending</div>
            <div className="kpi-value" style={{ color:'#9A6212' }}>{assignedIds.filter(id=>!reachedIds.includes(id)).length}</div>
          </div>
        </div>

        {/* Add contacts panel */}
        {showAddContacts && (
          <div className="card" style={{ marginBottom: 12, borderLeft: '3px solid var(--globant-green)', padding: '12px 14px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
              <div>
                <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--globant-green)' }}>➕ Add contacts to campaign</div>
                <div style={{ fontSize: 10, color: 'var(--globant-muted)', marginTop: 2 }}>
                  Campaign: <strong style={{ color: 'var(--globant-text)' }}>{F(selectedCampaign, 'Name')}</strong>
                </div>
              </div>
              <button className="action-btn btn-ghost" style={{ fontSize: 11 }} onClick={() => { setShowAddContacts(false); setAddContactsSearch(''); setSelectedToAdd(new Set()); }}>✕ Close</button>
            </div>
            <input
              className="input-field"
              style={{ width: '100%', fontSize: 12, marginBottom: 8 }}
              placeholder="Search by name, role, or account..."
              value={addContactsSearch}
              onChange={e => { setAddContactsSearch(e.target.value); setSelectedToAdd(new Set()); }}
              autoFocus
            />
            {availableToAdd.length > 0 && (
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8, padding: '4px 2px' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: 'var(--globant-muted)', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={selectedToAdd.size === availableToAdd.length}
                    onChange={e => setSelectedToAdd(e.target.checked ? new Set(availableToAdd.map(s => s.id)) : new Set())}
                  />
                  Select all ({availableToAdd.length})
                </label>
                {selectedToAdd.size > 0 && (
                  <button
                    className="action-btn btn-primary"
                    style={{ fontSize: 11 }}
                    disabled={bulkAdding}
                    onClick={addBulkContactsToCampaign}
                  >
                    {bulkAdding ? '⏳ Adding...' : `✅ Add ${selectedToAdd.size} contact${selectedToAdd.size > 1 ? 's' : ''} to "${F(selectedCampaign,'Name')}"`}
                  </button>
                )}
              </div>
            )}
            <div style={{ maxHeight: 280, overflowY: 'auto', border: '1px solid var(--globant-border)', borderRadius: 6 }}>
              {availableToAdd.length === 0 ? (
                <div style={{ padding: 14, textAlign: 'center', color: 'var(--globant-muted)', fontSize: 12 }}>
                  {addContactsSearch ? `No contacts match "${addContactsSearch}"` : 'All contacts are already assigned to this campaign'}
                </div>
              ) : (
                availableToAdd.map(s => {
                  const accId = linkedIds(s, 'Account')[0];
                  const acc = accId ? accounts.find(a => a.id === accId) : null;
                  const accName = acc ? F(acc, 'Account Name') : '';
                  const industry = acc ? F(acc, 'Industry') : '';
                  const isChecked = selectedToAdd.has(s.id);
                  return (
                    <div
                      key={s.id}
                      style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', borderBottom: '1px solid var(--globant-border)', cursor: 'pointer', background: isChecked ? 'rgba(91,191,181,0.06)' : 'transparent' }}
                      onClick={() => setSelectedToAdd(prev => { const n = new Set(prev); n.has(s.id) ? n.delete(s.id) : n.add(s.id); return n; })}
                    >
                      <input type="checkbox" checked={isChecked} onChange={() => {}} style={{ pointerEvents: 'none', flexShrink: 0 }} />
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 12, fontWeight: 600 }}>{F(s,'Name')}{F(s,'Last name') ? ` ${F(s,'Last name')}` : ''}</div>
                        <div style={{ fontSize: 10, color: 'var(--globant-muted)' }}>{F(s,'Role')}{accName ? ` · ${accName}` : ''}{industry ? ` · ${industry}` : ''}</div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        )}

        {/* Contact filters */}
        <div style={{ display:'flex', gap:8, marginBottom:12, flexWrap:'wrap' }}>
          <input className="input-field" style={{ fontSize:12, flex:1, minWidth:160 }} placeholder="Search assigned contacts..." value={filterSearch} onChange={e=>setFilterSearch(e.target.value)} />
          <select className="input-field" style={{ fontSize:12, minWidth:160 }} value={filterIndustry} onChange={e=>setFilterIndustry(e.target.value)}>
            <option value="">🏭 All industries</option>
            {industryOptions.map(i=><option key={i} value={i}>{i}</option>)}
          </select>
          <select className="input-field" style={{ fontSize:12, minWidth:160 }} value={filterRole} onChange={e=>setFilterRole(e.target.value)}>
            <option value="">👤 All roles</option>
            {roleOptions.map(r=><option key={r} value={r}>{r}</option>)}
          </select>
        </div>


        {/* Contacts list */}
        <div className="card" style={{ padding:0, overflow:'hidden' }}>
          <div style={{ padding:'10px 14px', borderBottom:'1px solid var(--globant-border)', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <span style={{ fontSize:13, fontWeight:700 }}>Assigned contacts ({detailContacts.length}{assignedIds.length !== detailContacts.length ? ` of ${assignedIds.length}` : ''})</span>
              <span style={{ fontSize:11, color:'var(--globant-muted)' }}>✅ = reached · ⏳ = pending</span>
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="action-btn btn-primary" style={{ fontSize: 11 }} onClick={() => setShowAddContacts(!showAddContacts)}>
                {showAddContacts ? '✕ Cancel' : '➕ Add contacts'}
              </button>
            </div>
          </div>
          {assignedIds.length === 0 ? (
            <div style={{ padding: 32, textAlign:'center', color:'var(--globant-muted)', fontSize:13 }}>
              <div style={{ fontSize: 32, marginBottom: 8 }}>👥</div>
              <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 6, color: 'var(--globant-text)' }}>No contacts assigned yet</div>
              <div style={{ marginBottom: 14 }}>Click <strong>➕ Add contacts</strong> above to select who you want to reach with this campaign.</div>
              <button className="action-btn btn-primary" style={{ fontSize: 12 }} onClick={() => setShowAddContacts(true)}>➕ Add contacts</button>
            </div>
          ) : detailContacts.length === 0 ? (
            <div style={{ padding:24, textAlign:'center', color:'var(--globant-muted)', fontSize:13 }}>No assigned contacts match the current filters.</div>
          ) : (
            <div style={{ maxHeight:540, overflowY:'auto' }}>
              {detailContacts.map(s => {
                const isReached = reachedIds.includes(s.id);
                const accId = linkedIds(s,'Account')[0];
                const acc = accounts.find(a=>a.id===accId);
                const accName = acc ? F(acc,'Account Name') : '';
                const industry = acc ? F(acc,'Industry') : '';
                const hasEmail = !!F(s,'Email');
                const hasPhone = !!F(s,'Phone number');
                const hasLinkedin = !!F(s,'LinkedIn');
                const isActive = invitePreview?.id === s.id;

                const isRemoving = addingContactId === s.id;
                return (
                  <div key={s.id} style={{ borderBottom:'1px solid var(--globant-border)', opacity: (isReached&&!isActive) || isRemoving ? 0.65 : 1 }}>
                    {/* Contact row */}
                    <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'10px 14px', gap: 8 }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ display:'flex', alignItems:'center', gap:6 }}>
                          {isReached ? (
                            <span style={{ fontSize: 9, fontWeight: 700, padding: '2px 7px', borderRadius: 10, background: 'rgba(74,222,128,0.15)', color: '#1E7B4F' }}>✅ Reached</span>
                          ) : (
                            <span style={{ fontSize: 9, fontWeight: 700, padding: '2px 7px', borderRadius: 10, background: 'rgba(251,191,36,0.15)', color: '#9A6212' }}>⏳ Pending</span>
                          )}
                          <span
                            style={{ fontWeight:600, fontSize:13, cursor:'pointer', color:'var(--globant-green)' }}
                            title="Open contact history"
                            onClick={() => setCampaignHistoryStk(s)}>
                            {F(s,'Name')}{F(s,'Last name') ? ` ${F(s,'Last name')}` : ''}
                          </span>
                          {F(s,'Level of Influence') && <span className="badge badge-accent" style={{ fontSize:9 }}>{F(s,'Level of Influence')}</span>}
                        </div>
                        <div style={{ fontSize:11, color:'var(--globant-muted)', marginTop:2 }}>
                          {F(s,'Role')}{accName ? ` · ${accName}` : ''}{industry ? ` · ${industry}` : ''}
                        </div>
                      </div>
                      <div style={{ display: 'flex', gap: 4, alignItems: 'center', flexWrap: 'wrap' }}>
                        <button
                          className="action-btn btn-primary" style={{ fontSize:10, padding:'4px 12px' }}
                          disabled={isRemoving}
                          onClick={() => goToMessageLab ? goToMessageLab(s.id) : setMsgModalStk(s)}>
                          {isReached ? '🔄 Resend' : '✨ Mensaje'}
                        </button>
                        <button
                          className="action-btn btn-ghost" style={{ fontSize: 10, padding: '4px 8px', color: '#B4362C', border: '1px solid rgba(239,68,68,0.3)' }}
                          disabled={isRemoving}
                          title="Remove from campaign"
                          onClick={() => removeContactFromCampaign(s.id)}>
                          {isRemoving ? '⏳' : '🗑️'}
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>


        {/* Edit modal (reuses create form) */}
        {showForm && (
          <div className="modal-overlay" onClick={()=>setShowForm(false)}>
            <div className="modal" onClick={e=>e.stopPropagation()} style={{ maxWidth:560 }}>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:18 }}>
                <h3 style={{ margin:0 }}>✏️ Edit Campaign</h3>
                <button onClick={()=>setShowForm(false)} style={{ background:'none', border:'none', color:'var(--globant-muted)', cursor:'pointer', fontSize:18 }}>✕</button>
              </div>
              <div style={{ display:'flex', flexDirection:'column', gap:12 }}>
                <div>
                  <label style={{ fontSize:11, color:'var(--globant-muted)', display:'block', marginBottom:4 }}>Campaign Name *</label>
                  <input style={inputSt} value={form.name} onChange={e=>setForm(p=>({...p,name:e.target.value}))} />
                </div>
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
                  <div>
                    <label style={{ fontSize:11, color:'var(--globant-muted)', display:'block', marginBottom:4 }}>Type</label>
                    <select style={inputSt} value={form.type} onChange={e=>setForm(p=>({...p,type:e.target.value}))}>
                      {TYPE_OPTIONS.map(t=><option key={t} value={t}>{t}</option>)}
                    </select>
                  </div>
                  <div>
                    <label style={{ fontSize:11, color:'var(--globant-muted)', display:'block', marginBottom:4 }}>Status</label>
                    <select style={inputSt} value={form.status} onChange={e=>setForm(p=>({...p,status:e.target.value}))}>
                      {STATUS_OPTIONS.map(s=><option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                </div>
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
                  <div>
                    <label style={{ fontSize:11, color:'var(--globant-muted)', display:'block', marginBottom:4 }}>Start Date</label>
                    <input type="date" style={inputSt} value={form.startDate} onChange={e=>setForm(p=>({...p,startDate:e.target.value}))} />
                  </div>
                  <div>
                    <label style={{ fontSize:11, color:'var(--globant-muted)', display:'block', marginBottom:4 }}>Goal (# contacts)</label>
                    <input type="number" style={inputSt} placeholder="e.g. 50" value={form.goal} onChange={e=>setForm(p=>({...p,goal:e.target.value}))} />
                  </div>
                </div>
                <div>
                  <label style={{ fontSize:11, color:'var(--globant-muted)', display:'block', marginBottom:4 }}>Asset URL (optional)</label>
                  <input style={inputSt} placeholder="https://..." value={form.assetUrl} onChange={e=>setForm(p=>({...p,assetUrl:e.target.value}))} />
                </div>
                <div>
                  <label style={{ fontSize:11, color:'var(--globant-muted)', display:'block', marginBottom:4 }}>Message Template / Reference Angle *</label>
                  <textarea style={{ ...inputSt, minHeight:100, resize:'vertical', fontFamily:'inherit' }}
                    placeholder="Core angle, key message, or talking points. The AI uses this as reference to personalize for each contact."
                    value={form.messageTemplate} onChange={e=>setForm(p=>({...p,messageTemplate:e.target.value}))} />
                </div>
                <div>
                  <label style={{ fontSize:11, color:'var(--globant-muted)', display:'block', marginBottom:4 }}>Campaign Context (strategic brief)</label>
                  <textarea style={{ ...inputSt, minHeight:120, resize:'vertical', fontFamily:'inherit' }}
                    placeholder="Target cohort, pain addressed, angle, desired outcome..."
                    value={form.context || ''} onChange={e=>setForm(p=>({...p,context:e.target.value}))} />
                </div>
                {form.assignedIds && form.assignedIds.length > 0 && (
                  <div style={{ padding: '10px 12px', background: 'rgba(16,185,129,0.08)', borderRadius: 6, border: '1px solid rgba(16,185,129,0.2)' }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: '#1E7B4F', marginBottom: 4 }}>👥 Pre-assigned stakeholders ({form.assignedIds.length})</div>
                    <div style={{ fontSize: 11, color: 'var(--globant-muted)' }}>
                      These contacts will be added to the campaign on save. You can remove any on the detail view after saving.
                    </div>
                  </div>
                )}
              </div>
              <div style={{ display:'flex', gap:8, marginTop:18 }}>
                <button className="action-btn btn-primary" style={{ fontSize:13 }} onClick={saveCampaign} disabled={saving}>{saving?'⏳ Saving...':'💾 Save Campaign'}</button>
                <button className="action-btn btn-ghost" style={{ fontSize:13 }} onClick={()=>setShowForm(false)}>Cancel</button>
              </div>
            </div>
          </div>
        )}

        {/* AI Message Modal */}
        {msgModalStk && (
          <AIMessageModal
            stakeholder={msgModalStk}
            onClose={() => setMsgModalStk(null)}
            onSend={(stakeholder, channel, msgRaw) => {
              const campAssetUrl = F(selectedCampaign,'Asset URL') || '';
              const message = campAssetUrl && !msgRaw.includes(campAssetUrl)
                ? `${msgRaw}\n\n🔗 ${campAssetUrl}`
                : msgRaw;
              const email = F(stakeholder,'Email')||'';
              const phone = F(stakeholder,'Phone number')||'';
              const linkedin = F(stakeholder,'LinkedIn')||'';
              let subject = '', body = message;
              if (channel === 'Email') {
                const lines = body.split('\n');
                const si = lines.findIndex(l => /^subject:/i.test(l.trim()));
                if (si !== -1) { subject = lines[si].replace(/^subject:\s*/i,'').trim(); body = lines.slice(si+1).join('\n').trim(); }
              }
              if (channel==='WhatsApp'&&phone) window.open(`https://wa.me/${String(phone).replace(/[^0-9+]/g,'')}?text=${encodeURIComponent(message)}`,'_blank');
              else if (channel==='Email'&&email) window.open(`https://mail.google.com/mail/?view=cm&to=${encodeURIComponent(email)}&su=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`,'_blank');
              else if (channel==='LinkedIn'&&linkedin) { navigator.clipboard.writeText(message).catch(()=>{}); window.open(linkedin,'_blank'); }
              // Log + mark reached
              const a = api || new AirtableAPI();
              const sName = `${F(stakeholder,'Name')||''} ${F(stakeholder,'Last name')||''}`.trim();
              const companyIds = linkedIds(stakeholder,'Account');
              a.createRecord(TABLE_IDS.outreach, {
                'Activity Name': `Campaign "${F(selectedCampaign,'Name')||''}": ${sName} — ${new Date().toLocaleDateString('en-US')}`,
                'Account': companyIds, 'Stakeholder': [stakeholder.id],
                'Channel': channel, 'Date': new Date().toISOString(), 'Status': 'Sent', 'Message': message,
                'Notes': `Campaign: ${F(selectedCampaign,'Name')||''} (${F(selectedCampaign,'Type')||''})`,
                'Logged By': CURRENT_USER?.name || '',
              }).then(async () => {
                const currentReached = linkedIds(selectedCampaign, 'Stakeholders Reached');
                const currentAssigned = linkedIds(selectedCampaign, 'Assigned Stakeholders');
                await a.updateRecord(TABLE_IDS.campaigns, selectedCampaign.id, {
                  'Stakeholders Reached': [...new Set([...currentReached, stakeholder.id])],
                  'Assigned Stakeholders': [...new Set([...currentAssigned, stakeholder.id])],
                }).catch(e => console.error('Campaign reached update failed:', e));
                if (onLogActivity) onLogActivity();
              }).catch(e => console.error('Campaign send log failed:', e));
              setMsgModalStk(null);
            }}
            onSuccess={() => { setMsgModalStk(null); if (onLogActivity) onLogActivity(); }}
            data={data}
          />
        )}

        {/* Stakeholder history modal — opens when clicking a contact name */}
        {campaignHistoryStk && (
          <StakeholderHistoryModal
            stakeholder={campaignHistoryStk}
            outreach={outreach}
            accounts={accounts}
            onClose={() => setCampaignHistoryStk(null)}
            onRefresh={onLogActivity}
            onAddRecord={onAddRecord}
            allData={data}
            onSend={(stakeholder, channel, message) => {
              // Reuse sendMsg — but since that uses invitePreview, build message directly via channel
              const email = F(stakeholder,'Email')||'';
              const phone = F(stakeholder,'Phone number')||'';
              const linkedin = F(stakeholder,'LinkedIn')||'';
              let subject = '', body = message;
              if (channel === 'Email') {
                const lines = body.split('\n');
                const si = lines.findIndex(l => /^subject:/i.test(l.trim()));
                if (si !== -1) { subject = lines[si].replace(/^subject:\s*/i,'').trim(); body = lines.slice(si+1).join('\n').trim(); }
              }
              if (channel==='WhatsApp'&&phone) window.open(`https://wa.me/${String(phone).replace(/[^0-9+]/g,'')}?text=${encodeURIComponent(message)}`,'_blank');
              else if (channel==='Email'&&email) window.open(`https://mail.google.com/mail/?view=cm&to=${encodeURIComponent(email)}&su=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`,'_blank');
              else if (channel==='LinkedIn'&&linkedin) { navigator.clipboard.writeText(message).catch(()=>{}); window.open(linkedin,'_blank'); }
            }}
          />
        )}
      </div>
    );
  }

  // ── LIST VIEW ──
  return (
    <div>
      {/* Hub Tab switcher */}
      <div style={{ display:'flex', gap:0, marginBottom:24, borderBottom:'1px solid var(--globant-border)' }}>
        {[['campaigns','📣 Campaigns'],['contentlab','✍️ Content Lab']].map(([k,label]) => (
          <button key={k} onClick={() => setHubTab(k)} style={{ padding:'10px 22px', background:'none', border:'none', borderBottom: hubTab===k ? '2px solid var(--globant-green)' : '2px solid transparent', color: hubTab===k ? 'var(--globant-green)' : 'var(--globant-muted)', fontWeight: hubTab===k ? 700 : 400, fontSize:14, cursor:'pointer', transition:'all 0.15s' }}>
            {label}
          </button>
        ))}
      </div>

      {hubTab === 'contentlab' && (
        <ContentLab data={data} api={api} onLogActivity={onLogActivity} onAddRecord={onAddRecord} onDeleteRecord={onDeleteRecord} />
      )}

      {hubTab === 'campaigns' && <div>
      <div style={{ display:'flex', gap:8, marginBottom:16, flexWrap:'wrap' }}>
        <input className="input-field" style={{ fontSize:12, flex:1, minWidth:180 }} placeholder="Search campaigns..." value={listSearch} onChange={e=>setListSearch(e.target.value)} />
        <select className="input-field" style={{ fontSize:12, minWidth:140 }} value={statusFilter} onChange={e=>setStatusFilter(e.target.value)}>
          <option value="">All statuses</option>
          {STATUS_OPTIONS.map(s=><option key={s} value={s}>{s}</option>)}
        </select>
        <button className="action-btn btn-primary" style={{ fontSize:12 }} onClick={openCreate}>+ New Campaign</button>
      </div>

      {filteredCampaigns.length === 0 ? (
        <div className="card" style={{ textAlign:'center', padding:40 }}>
          <div style={{ fontSize:32, marginBottom:12 }}>📣</div>
          <div style={{ fontSize:15, fontWeight:700, marginBottom:8 }}>{campaigns.length===0 ? 'No campaigns yet' : 'No campaigns match'}</div>
          <div style={{ fontSize:13, color:'var(--globant-muted)', marginBottom:16 }}>{campaigns.length===0 ? 'Create your first campaign to start targeted outreach' : 'Try adjusting your filters'}</div>
          {campaigns.length===0 && <button className="action-btn btn-primary" onClick={openCreate}>+ New Campaign</button>}
        </div>
      ) : (
        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(300px,1fr))', gap:14 }}>
          {filteredCampaigns.map(c => {
            const status = F(c,'Status') || '';
            const type = F(c,'Type') || '';
            const reached = linkedIds(c,'Stakeholders Reached').length;
            const cardAssignedRaw = linkedIds(c,'Assigned Stakeholders');
            const assigned = [...new Set([...cardAssignedRaw, ...linkedIds(c,'Stakeholders Reached')])].length;
            const goal = c.fields?.['Goal'] || 0;
            const pct = goal>0 ? Math.min(100, Math.round((reached/goal)*100)) : null;
            return (
              <div key={c.id} className="card" style={{ cursor:'pointer', borderTop:`3px solid ${STATUS_COLORS[status]||'var(--globant-border)'}` }}
                onClick={()=>{ setSelectedId(c.id); setInvitePreview(null); }}>
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'start', marginBottom:8 }}>
                  <div style={{ display:'flex', alignItems:'center', gap:6 }}>
                    <span style={{ fontSize:18 }}>{TYPE_ICONS[type]||'📣'}</span>
                    <span style={{ fontWeight:700, fontSize:14 }}>{F(c,'Name')}</span>
                  </div>
                  <div style={{ display:'flex', alignItems:'center', gap:6 }}>
                    <span style={{ fontSize:10, padding:'3px 8px', borderRadius:20, background:`${STATUS_COLORS[status]||'#666'}20`, color:STATUS_COLORS[status]||'var(--globant-muted)', fontWeight:700, whiteSpace:'nowrap', border:`1px solid ${STATUS_COLORS[status]||'var(--globant-border)'}40` }}>{status}</span>
                    <button
                      className="action-btn btn-ghost"
                      style={{ fontSize:10, color:'#B4362C', padding:'2px 6px', border:'1px solid rgba(239,68,68,0.3)' }}
                      onClick={e => { e.stopPropagation(); deleteCampaign(c); }}
                      title="Delete campaign"
                    >🗑️</button>
                  </div>
                </div>
                <div style={{ fontSize:11, color:'var(--globant-muted)', marginBottom:8 }}>
                  {type}{c.fields?.['Start Date'] ? ` · ${formatDate(c.fields['Start Date'])}` : ''}
                </div>
                {F(c,'Message Template') && (
                  <div style={{ fontSize:11, color:'var(--globant-muted)', fontStyle:'italic', marginBottom:10, lineHeight:1.4 }}>
                    "{(F(c,'Message Template')||'').slice(0,100)}{(F(c,'Message Template')||'').length>100?'...':''}"
                  </div>
                )}
                <div style={{ display:'flex', alignItems:'center', gap:10, flexWrap: 'wrap' }}>
                  <span style={{ fontSize:12, color:'var(--globant-muted)', fontWeight:600 }}>👥 {assigned} assigned</span>
                  <span style={{ fontSize:12, color:'#1E7B4F', fontWeight:700 }}>✅ {reached} reached</span>
                  {goal>0 && <span style={{ fontSize:11, color:'var(--globant-muted)' }}>/ {goal} goal</span>}
                </div>
                {pct!==null && (
                  <div style={{ height:4, borderRadius:2, background:'var(--globant-darker)', overflow:'hidden', marginTop:8 }}>
                    <div style={{ height:'100%', width:`${pct}%`, background:'linear-gradient(90deg,#1E7B4F,#22d3ee)', borderRadius:2 }} />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Create modal */}
      {showForm && (
        <div className="modal-overlay" onClick={()=>setShowForm(false)}>
          <div className="modal" onClick={e=>e.stopPropagation()} style={{ maxWidth:560 }}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:18 }}>
              <h3 style={{ margin:0 }}>📣 New Campaign</h3>
              <button onClick={()=>setShowForm(false)} style={{ background:'none', border:'none', color:'var(--globant-muted)', cursor:'pointer', fontSize:18 }}>✕</button>
            </div>
            <div style={{ display:'flex', flexDirection:'column', gap:12 }}>
              <div>
                <label style={{ fontSize:11, color:'var(--globant-muted)', display:'block', marginBottom:4 }}>Campaign Name *</label>
                <input style={inputSt} placeholder="e.g. White Paper Q2 — Real Estate" value={form.name} onChange={e=>setForm(p=>({...p,name:e.target.value}))} />
              </div>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
                <div>
                  <label style={{ fontSize:11, color:'var(--globant-muted)', display:'block', marginBottom:4 }}>Type</label>
                  <select style={inputSt} value={form.type} onChange={e=>setForm(p=>({...p,type:e.target.value}))}>
                    {TYPE_OPTIONS.map(t=><option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
                <div>
                  <label style={{ fontSize:11, color:'var(--globant-muted)', display:'block', marginBottom:4 }}>Status</label>
                  <select style={inputSt} value={form.status} onChange={e=>setForm(p=>({...p,status:e.target.value}))}>
                    {STATUS_OPTIONS.map(s=><option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
              </div>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
                <div>
                  <label style={{ fontSize:11, color:'var(--globant-muted)', display:'block', marginBottom:4 }}>Start Date</label>
                  <input type="date" style={inputSt} value={form.startDate} onChange={e=>setForm(p=>({...p,startDate:e.target.value}))} />
                </div>
                <div>
                  <label style={{ fontSize:11, color:'var(--globant-muted)', display:'block', marginBottom:4 }}>Goal (# contacts)</label>
                  <input type="number" style={inputSt} placeholder="e.g. 50" value={form.goal} onChange={e=>setForm(p=>({...p,goal:e.target.value}))} />
                </div>
              </div>
              <div>
                <label style={{ fontSize:11, color:'var(--globant-muted)', display:'block', marginBottom:4 }}>Asset URL (optional)</label>
                <input style={inputSt} placeholder="https://..." value={form.assetUrl} onChange={e=>setForm(p=>({...p,assetUrl:e.target.value}))} />
              </div>
              <div>
                <label style={{ fontSize:11, color:'var(--globant-muted)', display:'block', marginBottom:4 }}>Message Template / Reference Angle *</label>
                <textarea style={{ ...inputSt, minHeight:100, resize:'vertical', fontFamily:'inherit' }}
                  placeholder="Core angle, key message, or talking points. The AI uses this as reference to personalize for each contact."
                  value={form.messageTemplate} onChange={e=>setForm(p=>({...p,messageTemplate:e.target.value}))} />
              </div>
              <div>
                <label style={{ fontSize:11, color:'var(--globant-muted)', display:'block', marginBottom:4 }}>Campaign Context (strategic brief)</label>
                <textarea style={{ ...inputSt, minHeight:120, resize:'vertical', fontFamily:'inherit' }}
                  placeholder="Target cohort, pain addressed, angle, desired outcome..."
                  value={form.context || ''} onChange={e=>setForm(p=>({...p,context:e.target.value}))} />
              </div>
              {form.assignedIds && form.assignedIds.length > 0 && (
                <div style={{ padding: '10px 12px', background: 'rgba(16,185,129,0.08)', borderRadius: 6, border: '1px solid rgba(16,185,129,0.2)' }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: '#1E7B4F', marginBottom: 4 }}>👥 Pre-assigned stakeholders ({form.assignedIds.length})</div>
                  <div style={{ fontSize: 11, color: 'var(--globant-muted)' }}>
                    These contacts will be added to the campaign on save.
                  </div>
                </div>
              )}
            </div>
            <div style={{ display:'flex', gap:8, marginTop:18 }}>
              <button className="action-btn btn-primary" style={{ fontSize:13 }} onClick={saveCampaign} disabled={saving}>{saving?'⏳ Saving...':'💾 Save Campaign'}</button>
              <button className="action-btn btn-ghost" style={{ fontSize:13 }} onClick={()=>setShowForm(false)}>Cancel</button>
            </div>
          </div>
        </div>
      )}
      </div>}
    </div>
  );
}

// ============ CONTENT LAB ============

export default CampaignsHub;
